"use client";
import React from "react";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import VendorSwitchModal from "./VendorSwitchModal";
const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    fontSize: "12px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderRadius: "4px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "11px",
    padding: "1px 4px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": {
      backgroundColor: "#f97316",
      color: "#fff",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    zIndex: 50,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
    flexWrap: "wrap",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
};

const multiSelectFilterStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#1f2937",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    minHeight: "50px",
    zIndex: 100000,
    fontSize: "14px",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "14px",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 999999,
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#374151",
    borderRadius: "0.5rem",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
    border: "1px solid #4b5563",
    width: "300px",
    maxWidth: "300px",
    minWidth: "260px",
    zIndex: 999999,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "380px",
    padding: "8px",
    overflowY: "auto",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    padding: "10px 12px",
    cursor: "pointer",
    fontSize: "13.5px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: "1.4",
    "&:hover": {
      backgroundColor: "#4b5563",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "4px 8px",
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    alignItems: "flex-start",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#4b5563",
    borderRadius: "6px",
    margin: 0,
    fontSize: "13px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#e5e7eb",
    padding: "3px 8px",
    paddingRight: "6px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "150px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#9ca3af",
    cursor: "pointer",
    paddingLeft: "4px",
    paddingRight: "4px",
    borderRadius: "4px",
    ":hover": {
      backgroundColor: "#f97316",
      color: "white",
    },
  }),
};

// ─── Search helpers ────────────────────────────────────────────────────────────
function normaliseSearch(str) {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .replace(/[''"""]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function buildPoSearchBlob(item) {
  const parts = [
    item.description || "",
    item.conditionName || "",
    item.sectionNumber || "",
    item.size || "",
    item.vendorName || "",
    item.bidArea || "",
    item.laborId || "",
  ];
  return normaliseSearch(parts.join("  "));
}

function tokenVariants(token) {
  const variants = [token];
  const fractionCondensed = token.match(/^(\d+)(\d)(\d)$/);
  if (fractionCondensed) {
    const [, a, b, c] = fractionCondensed;
    variants.push(`${a}-${b}/${c}`);
    variants.push(`${a} ${b}/${c}`);
    variants.push(`${a}${b}/${c}`);
  }
  const fractionExpanded = token.match(/^(\d+)[\s-]?(\d)\/(\d)$/);
  if (fractionExpanded) {
    const [, a, b, c] = fractionExpanded;
    variants.push(`${a}${b}${c}`);
  }
  const gaugeMatch = token.match(/^(\d+)(ga|gauge)$/i);
  if (gaugeMatch) {
    const [, num] = gaugeMatch;
    variants.push(`${num}ga`);
    variants.push(`${num} ga`);
    variants.push(`${num}gauge`);
  }
  return variants;
}

function multiTokenMatch(blob, searchStr) {
  const raw = normaliseSearch(searchStr);
  if (!raw) return true;
  const tokens = raw.split(/\s+/).filter(Boolean);
  return tokens.every((token) =>
    tokenVariants(token).some((v) => blob.includes(v)),
  );
}
// ──────────────────────────────────────────────────────────────────────────────

const getSafeCoverage = (coverage) => {
  if (
    coverage === null ||
    coverage === undefined ||
    coverage === "" ||
    coverage === "-" ||
    String(coverage).trim().toUpperCase() === "N/A" ||
    String(coverage).trim().toUpperCase() === "NONE"
  ) {
    return 1;
  }
  const num = Number(coverage);
  return isNaN(num) || num <= 0 ? 1 : num;
};

const isSFUnit = (uom) => {
  const u = (uom || "").toLowerCase();
  return (
    u === "sf" || u.includes("square") || u.includes("sq ft") || u === "sheet"
  );
};

const isLFUnit = (uom) => {
  const u = (uom || "").toLowerCase();
  return u === "lf" || u.includes("linear") || u.includes("lin ft");
};

const normalizeConditionName = (conditionName) => {
  if (!conditionName) return conditionName;
  const str = String(conditionName).trim();
  const match = str.match(/^\d+\s*[-–—]\s*(.+)$/);
  return match ? match[1].trim() : str;
};

// Sort icon component
const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig.key !== column) {
    return <span className="ml-0.5 text-gray-600 text-[10px]">⇅</span>;
  }
  return (
    <span className="ml-0.5 text-orange-400 text-[10px]">
      {sortConfig.dir === "asc" ? "↑" : "↓"}
    </span>
  );
};

// ─── MO Status Badge ──────────────────────────────────────────────────────────
const MOStatusBadge = ({ status }) => {
  const config = {
    pending: {
      bg: "bg-yellow-500/20",
      text: "text-yellow-300",
      border: "border-yellow-500/40",
      label: "Pending",
    },
    confirmed: {
      bg: "bg-blue-500/20",
      text: "text-blue-300",
      border: "border-blue-500/40",
      label: "Confirmed",
    },
    delivered: {
      bg: "bg-green-500/20",
      text: "text-green-300",
      border: "border-green-500/40",
      label: "Delivered",
    },
    void: {
      bg: "bg-red-500/20",
      text: "text-red-300",
      border: "border-red-500/40",
      label: "Void",
    },
  };
  const c = config[status] || {
    bg: "bg-gray-500/20",
    text: "text-gray-300",
    border: "border-gray-500/40",
    label: status,
  };
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide ${c.bg} ${c.text} ${c.border}`}
    >
      {c.label}
    </span>
  );
};

// Extract MO number from variance budget title like "Variance - MO# 38" or "Stock-Out Variance - MO# 44"
const extractMONumberFromTitle = (title) => {
  if (!title) return null;
  const match = String(title).match(/MO#\s*(\d+)/i);
  return match ? match[1] : null;
};

// ─── Expandable MO breakdown row ─────────────────────────────────────────────
const MOBreakdownRow = ({
  lineItemId,
  projectId,
  colSpan,
  navigate,
  id,
  formatNumber,
  vendorId,
  isHistoricalPriceLine,
  isPriceChangeRow,
  isNewSpec,
  closedOutBySizeChange,
  rowQtyOrdered,
}) => {
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetch_ = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API_BASE}/materialOrders2/project/${projectId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (cancelled) return;

        // Step 1: Filter to MOs containing this exact lineItemId
        let relevant = (res.data || []).filter(
          (mo) =>
            mo.status !== "void" &&
            mo.items?.some(
              (item) => item.lineItemId?.toString() === lineItemId?.toString(),
            ),
        );

        // Step 2: For price-change / stock-out split rows, filter by vendor
        if (
          vendorId &&
          (isPriceChangeRow ||
            isNewSpec ||
            closedOutBySizeChange ||
            isHistoricalPriceLine)
        ) {
          relevant = relevant.filter((mo) => {
            const moVendorId =
              typeof mo.vendor === "object"
                ? mo.vendor?._id?.toString()
                : mo.vendor?.toString();
            return moVendorId === vendorId;
          });
        }

        // Step 3: For new-spec rows with a known qtyOrdered, match MOs
        // by the specific item quantity to disambiguate multiple new-spec
        // rows from different stock-out deliveries
        if (isNewSpec && rowQtyOrdered > 0 && relevant.length > 1) {
          const qtyMatched = relevant.filter((mo) => {
            const moItem = mo.items?.find(
              (item) => item.lineItemId?.toString() === lineItemId?.toString(),
            );
            return moItem && moItem.quantity === rowQtyOrdered;
          });

          // Only use qty matching if it narrows results (don't lose all MOs)
          if (qtyMatched.length > 0 && qtyMatched.length < relevant.length) {
            relevant = qtyMatched;
          }
        }

        setOrders(relevant);
      } catch {
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch_();
    return () => {
      cancelled = true;
    };
  }, [
    lineItemId,
    projectId,
    vendorId,
    isHistoricalPriceLine,
    isPriceChangeRow,
    isNewSpec,
    closedOutBySizeChange,
    rowQtyOrdered,
  ]);

  return (
    <tr>
      <td colSpan={colSpan} className="px-0 py-0">
        <div className="mx-3 mb-3 rounded-lg border border-gray-600/60 bg-gray-900/60 overflow-hidden">
          {/* header */}
          <div className="px-4 py-2 bg-gray-800/80 border-b border-gray-600/40 flex items-center gap-2">
            <svg
              className="w-3.5 h-3.5 text-orange-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <span className="text-[11px] font-semibold text-gray-300 uppercase tracking-wider">
              Material Orders
            </span>
          </div>

          {/* body */}
          <div className="px-3 py-2">
            {loading ? (
              <div className="flex items-center gap-2 py-2">
                <div className="w-3 h-3 rounded-full border-2 border-orange-400 border-t-transparent animate-spin" />
                <span className="text-[11px] text-gray-500">
                  Loading orders…
                </span>
              </div>
            ) : !orders || orders.length === 0 ? (
              <div className="py-2 text-[11px] text-gray-500 italic">
                No material orders found for this item.
              </div>
            ) : (
              <div className="flex flex-col gap-1.5 py-1">
                {orders.map((mo) => {
                  const moItem = mo.items?.find(
                    (item) =>
                      item.lineItemId?.toString() === lineItemId?.toString(),
                  );
                  const qtyOnThisOrder = moItem?.quantity ?? 0;

                  const vendorName =
                    typeof mo.vendor === "object"
                      ? mo.vendor?.companyName
                      : null;

                  const deliveryDate = mo.expectedDeliveryDate
                    ? new Date(mo.expectedDeliveryDate).toLocaleDateString(
                        "en-US",
                        {
                          timeZone: "UTC",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        },
                      )
                    : null;

                  const createdDate = mo.createdAt
                    ? new Date(mo.createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : null;

                  return (
                    <div
                      key={mo._id}
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-gray-800/50 border border-gray-700/50 hover:border-gray-600/80 transition-colors"
                    >
                      {/* Left: MO# + status */}
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[12px] font-bold text-orange-300 font-mono whitespace-nowrap">
                          MO# {mo.materialOrderNumber}
                        </span>
                        <MOStatusBadge status={mo.status} />
                        {mo.isStockOut && (
                          <span className="text-[9px] px-1.5 py-0.5 bg-purple-500/20 text-purple-300 border border-purple-500/40 rounded font-medium">
                            STOCK-OUT
                          </span>
                        )}
                      </div>

                      {/* Middle: qty + vendor + date */}
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-gray-500">
                            Qty:
                          </span>
                          <span className="text-[12px] text-white font-semibold">
                            {formatNumber(qtyOnThisOrder)}
                          </span>
                        </div>

                        {vendorName && (
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="text-[10px] text-gray-500">
                              Vendor:
                            </span>
                            <span className="text-[11px] text-gray-300 truncate max-w-[120px]">
                              {vendorName}
                            </span>
                          </div>
                        )}

                        {(deliveryDate || createdDate) && (
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-gray-500">
                              {deliveryDate ? "Delivery:" : "Created:"}
                            </span>
                            <span className="text-[11px] text-gray-300 whitespace-nowrap">
                              {deliveryDate || createdDate}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Right: View Order button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/Projects/${id}/material-order2/${mo._id}`);
                        }}
                        className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-orange-400 border border-orange-500/40 rounded hover:bg-orange-500/10 hover:border-orange-400 transition-colors whitespace-nowrap flex-shrink-0"
                      >
                        View Order
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

export default function PODetailView() {
  const { id, poId } = useParams();
  const navigate = useNavigate();

  // Data state
  const [po, setPO] = useState(null);
  const [poItems, setPoItems] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [groupBySimilar, setGroupBySimilar] = useState(false);
  const [showVendorSwitchModal, setShowVendorSwitchModal] = useState(false);
  const [selectedForVendorSwitch, setSelectedForVendorSwitch] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState(new Set());
  // Edit state
  const [editing, setEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [editedCostCode, setEditedCostCode] = useState("");
  const filterRefs = useRef({});
  const [editedPrimaryVendor, setEditedPrimaryVendor] = useState(null);
  const [editedNotes, setEditedNotes] = useState("");
  const [itemsToAdd, setItemsToAdd] = useState([]);
  const [itemsToRemove, setItemsToRemove] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  // Sort state for the main PO table
  const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" });

  // Quick filter badge state
  const [quickFilter, setQuickFilter] = useState(null);

  // ─── Expandable rows state ────────────────────────────────────────────────
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpand = useCallback((lineItemId, e) => {
    // Don't expand if clicking a button or input, or if in edit mode
    if (e && (e.target.closest("button") || e.target.closest("input"))) return;
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(lineItemId)) {
        next.delete(lineItemId);
      } else {
        next.add(lineItemId);
      }
      return next;
    });
  }, []);
  // ──────────────────────────────────────────────────────────────────────────
  const toggleRowSelect = useCallback((rowKey, e) => {
    if (e) e.stopPropagation();
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);
  // Filter state for PO items view
  const [poFilters, setPoFilters] = useState({
    conditionName: [],
    sectionNumber: [],
    laborId: [],
    size: [],
    vendor: [],
    bidArea: [],
    descriptionSearch: "",
  });

  const [poTableFilters, setPoTableFilters] = useState({
    conditionName: [],
    sectionNumber: [],
    size: [],
    vendor: [],
    bidArea: [],
    descriptionSearch: "",
  });

  const registerFilterRef = (field, ref) => {
    if (ref) {
      filterRefs.current[field] = ref;
    } else {
      delete filterRefs.current[field];
    }
  };

  const [showPoTableFilter, setShowPoTableFilter] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showPoTableFilter) return;

      const activeRef = filterRefs.current[showPoTableFilter];
      if (activeRef?.current?.contains(event.target)) return;

      const isReactSelectClick =
        event.target.closest('[class*="css-"]') ||
        event.target.closest(".react-select__menu") ||
        event.target.closest(".react-select__menu-portal") ||
        event.target.closest(".react-select__option") ||
        event.target.id?.startsWith("react-select");

      if (isReactSelectClick) return;

      setShowPoTableFilter(null);
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [showPoTableFilter]);

  const poTableConditionRef = useRef(null);
  const poTableSectionRef = useRef(null);
  const poTableSizeRef = useRef(null);
  const poTableVendorRef = useRef(null);
  const poTableBidAreaRef = useRef(null);
  const poTableDescriptionRef = useRef(null);

  const poTableFilterOptions = useMemo(() => {
    const materials = poItems.filter((item) => item.category === "M");
    return {
      conditionName: [
        ...new Set(
          materials
            .map((i) => normalizeConditionName(i.conditionName))
            .filter(Boolean),
        ),
      ].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
      ),
      sectionNumber: [
        ...new Set(materials.map((i) => i.sectionNumber).filter(Boolean)),
      ].sort(),
      size: [...new Set(materials.map((i) => i.size).filter(Boolean))].sort(),
      vendor: [
        ...new Set(
          materials.map((i) => i.vendorName || "Unassigned").filter(Boolean),
        ),
      ].sort(),
      bidArea: [
        ...new Set(materials.map((i) => i.bidArea).filter(Boolean)),
      ].sort(),
    };
  }, [poItems]);

  // Handle column sort toggle
  const handleSort = useCallback((key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: "desc" };
    });
  }, []);

  const filteredPoItems = useMemo(() => {
    const materials = poItems.filter((item) => item.category === "M");

    let rows = materials.filter((item) => {
      const normalizedCond = normalizeConditionName(item.conditionName);

      if (
        poTableFilters.conditionName.length > 0 &&
        !poTableFilters.conditionName.includes(normalizedCond)
      )
        return false;
      if (
        poTableFilters.sectionNumber.length > 0 &&
        !poTableFilters.sectionNumber.includes(item.sectionNumber)
      )
        return false;
      if (
        poTableFilters.size.length > 0 &&
        !poTableFilters.size.includes(item.size)
      )
        return false;
      if (
        poTableFilters.vendor.length > 0 &&
        !poTableFilters.vendor.includes(item.vendorName || "Unassigned")
      )
        return false;
      if (
        poTableFilters.bidArea.length > 0 &&
        !poTableFilters.bidArea.includes(item.bidArea)
      )
        return false;
      if (poTableFilters.descriptionSearch) {
        const blob = buildPoSearchBlob(item);
        if (!multiTokenMatch(blob, poTableFilters.descriptionSearch))
          return false;
      }

      if (quickFilter === "available") {
        if (!(Number(item.remaining || 0) > 0)) return false;
      }
      if (quickFilter === "fullyOrdered") {
        const netQty = Number(item.requiredQty || 0);
        const ordered = Number(item.qtyOrdered || 0);
        if (!(netQty > 0 && ordered >= netQty)) return false;
      }
      if (quickFilter === "zeroRemaining") {
        if (Number(item.remaining || 0) !== 0) return false;
      }
      if (quickFilter === "pending") {
        if (!(Number(item.qtyPending || 0) > 0)) return false;
      }

      return true;
    });

    if (sortConfig.key) {
      rows = [...rows].sort((a, b) => {
        let aVal, bVal;
        switch (sortConfig.key) {
          case "requiredQty":
            aVal = Number(a.requiredQty || 0);
            bVal = Number(b.requiredQty || 0);
            break;
          case "qtyPending":
            aVal = Number(a.qtyPending || 0);
            bVal = Number(b.qtyPending || 0);
            break;
          case "qtyOrdered":
            aVal = Number(a.qtyOrdered || 0);
            bVal = Number(b.qtyOrdered || 0);
            break;
          case "remaining":
            aVal = Number(a.remaining || 0);
            bVal = Number(b.remaining || 0);
            break;
          case "price":
            aVal = Number(a.price || 0);
            bVal = Number(b.price || 0);
            break;
          case "amount":
            aVal = Number(a.amount || 0);
            bVal = Number(b.amount || 0);
            break;
          default:
            aVal = 0;
            bVal = 0;
        }
        return sortConfig.dir === "asc" ? aVal - bVal : bVal - aVal;
      });
    } else {
      rows = [...rows].sort((a, b) => {
        const condA = normalizeConditionName(a.conditionName) || "ZZZZ";
        const condB = normalizeConditionName(b.conditionName) || "ZZZZ";
        if (condA !== condB) {
          return condA.localeCompare(condB, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        }
        return (a.sectionNumber || "").localeCompare(b.sectionNumber || "");
      });
    }

    return rows;
  }, [poItems, poTableFilters, quickFilter, sortConfig]);

  const quickFilterCounts = useMemo(() => {
    const materials = poItems.filter((item) => item.category === "M");
    const baseFiltered = materials.filter((item) => {
      const normalizedCond = normalizeConditionName(item.conditionName);
      if (
        poTableFilters.conditionName.length > 0 &&
        !poTableFilters.conditionName.includes(normalizedCond)
      )
        return false;
      if (
        poTableFilters.sectionNumber.length > 0 &&
        !poTableFilters.sectionNumber.includes(item.sectionNumber)
      )
        return false;
      if (
        poTableFilters.size.length > 0 &&
        !poTableFilters.size.includes(item.size)
      )
        return false;
      if (
        poTableFilters.vendor.length > 0 &&
        !poTableFilters.vendor.includes(item.vendorName || "Unassigned")
      )
        return false;
      if (
        poTableFilters.bidArea.length > 0 &&
        !poTableFilters.bidArea.includes(item.bidArea)
      )
        return false;
      if (poTableFilters.descriptionSearch) {
        const blob = buildPoSearchBlob(item);
        if (!multiTokenMatch(blob, poTableFilters.descriptionSearch))
          return false;
      }
      return true;
    });

    return {
      available: baseFiltered.filter((i) => Number(i.remaining || 0) > 0)
        .length,
      fullyOrdered: baseFiltered.filter((i) => {
        const netQty = Number(i.requiredQty || 0);
        const ordered = Number(i.qtyOrdered || 0);
        return netQty > 0 && ordered >= netQty;
      }).length,
      zeroRemaining: baseFiltered.filter((i) => Number(i.remaining || 0) === 0)
        .length,
      pending: baseFiltered.filter((i) => Number(i.qtyPending || 0) > 0).length,
      total: baseFiltered.length,
    };
  }, [poItems, poTableFilters]);

  const displayRows = useMemo(() => {
    if (!groupBySimilar) {
      return filteredPoItems;
    }

    const groups = {};

    filteredPoItems.forEach((item) => {
      const key = [
        item.description?.trim() || "—",
        item.size?.trim() || "—",
        item.vendorName || "Unassigned",
      ].join("|||");

      if (!groups[key]) {
        groups[key] = {
          key,
          description: item.description || "—",
          size: item.size || "—",
          vendorName: item.vendorName || "Unassigned",
          totalNetQty: 0,
          totalPending: 0,
          totalOrdered: 0,
          totalRemaining: 0,
          totalAmount: 0,
          price: item.price,
          coveragePerUnit: item.coveragePerUnit,
          uom: item.uom,
          count: 0,
          sampleCondition: item.conditionName || "—",
          sampleSection: item.sectionNumber || "—",
        };
      }

      const g = groups[key];
      g.totalNetQty += Number(item.requiredQty || 0);
      g.totalPending += Number(item.qtyPending || 0);
      g.totalOrdered += Number(item.qtyOrdered || 0);
      g.totalRemaining += Number(item.remaining || 0);
      g.totalAmount += Number(item.amount || 0);
      g.count++;
    });

    return Object.values(groups).sort((a, b) => {
      if (sortConfig.key && groupBySimilar) {
        let aVal, bVal;
        switch (sortConfig.key) {
          case "requiredQty":
            aVal = a.totalNetQty;
            bVal = b.totalNetQty;
            break;
          case "qtyPending":
            aVal = a.totalPending;
            bVal = b.totalPending;
            break;
          case "qtyOrdered":
            aVal = a.totalOrdered;
            bVal = b.totalOrdered;
            break;
          case "remaining":
            aVal = a.totalRemaining;
            bVal = b.totalRemaining;
            break;
          case "amount":
            aVal = a.totalAmount;
            bVal = b.totalAmount;
            break;
          default:
            aVal = 0;
            bVal = 0;
        }
        return sortConfig.dir === "asc" ? aVal - bVal : bVal - aVal;
      }
      if (a.description !== b.description)
        return a.description.localeCompare(b.description);
      if (a.size !== b.size) return a.size.localeCompare(b.size);
      return a.vendorName.localeCompare(b.vendorName);
    });
  }, [filteredPoItems, groupBySimilar, sortConfig]);

  const materialTotals = useMemo(() => {
    return displayRows.reduce(
      (acc, row) => {
        const qty = groupBySimilar
          ? row.totalNetQty
          : Number(row.requiredQty ?? 0);
        const amount = groupBySimilar
          ? row.totalAmount
          : Number(row.amount ?? 0);

        return {
          count: acc.count + 1,
          totalAmount: acc.totalAmount + amount,
          totalQty: acc.totalQty + qty,
        };
      },
      { count: 0, totalAmount: 0, totalQty: 0 },
    );
  }, [displayRows, groupBySimilar]);

  const [showPoFilter, setShowPoFilter] = useState(null);
  const [poSearchInput, setPoSearchInput] = useState("");
  const poSearchTimeoutRef = useRef(null);

  const poConditionRef = useRef(null);
  const poSectionRef = useRef(null);
  const poLaborIdRef = useRef(null);
  const poSizeRef = useRef(null);
  const poVendorRef = useRef(null);
  const poBidAreaRef = useRef(null);
  const poDescriptionRef = useRef(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const [vendorsRes, costCodesRes] = await Promise.all([
          axios.get(`${API_BASE}/accounts/vendors`, { headers }),
          axios.get(`${API_BASE}/costCodes`, { headers }),
        ]);

        setCostCodes(costCodesRes.data);

        const vendorsList = vendorsRes.data;
        setVendors(vendorsList);

        const poRes = await axios.get(`${API_BASE}/purchaseOrders2/${poId}`, {
          headers,
        });
        const poData = poRes.data;
        const currentProjectId = poData.project?._id || poData.project || id;
        setProjectId(currentProjectId);

        const netRes = await axios.get(
          `${API_BASE}/net-assemblies/project/${currentProjectId}`,
          {
            headers,
            params: {
              category: "M",
              linkedPO: poId,
              includePrices: true,
            },
          },
        );

        const netData = netRes.data.data || [];

        const getVendorName = (vendorRef) => {
          if (!vendorRef) return "Unassigned";
          if (typeof vendorRef === "object" && vendorRef.companyName) {
            return vendorRef.companyName;
          }
          const vendorId =
            typeof vendorRef === "object" ? vendorRef._id : vendorRef;
          if (!vendorId) return "Unassigned";
          const vendor = vendorsList.find((v) => v._id === vendorId);
          return vendor?.companyName || "Unassigned";
        };

        const mappedItems = netData.flatMap((item) => {
          const qty = Number(item.netRequiredQty || 0);
          const coverage = getSafeCoverage(
            item.coveragePerUnit ?? item.latestCoveragePerUnit,
          );
          const price = Number(item.latestPrice ?? item.price ?? 0);
          const calculatedAmount = qty * coverage * price;

          const base = {
            ...item,
            assemblyId: item.assemblyId,
            sourceMONumber: extractMONumberFromTitle(
              item._sourceBudgetTitle || "",
            ),
            _id: item.lineItemId,
            lineItemId: (
              item.originalLineItemId ||
              item.lineItemId ||
              item.key ||
              ""
            ).toString(),
            originalLineItemId: item.originalLineItemId,
            vendorId: item.vendor?._id || item.vendor,
            vendorName: getVendorName(item.vendor),
            description: item.costCodeDescription || item.itemDescription || "",
            conditionName: item.conditionName || "",
            sectionNumber: item.sectionNumber || "",
            laborId: item.laborId || null,
            size: item.latestSize || item.size || "",
            bidArea: item.bidArea || "",
            requiredQty: qty,
            qtyPending: item.qtyPending || 0,
            qtyOrdered: item.qtyOrdered || 0,
            remaining: item.netRemaining || 0,
            price,
            coveragePerUnit: coverage,
            amount: calculatedAmount,
            isHistoricalPriceLine: !!item.isHistoricalPriceLine,
            isPriceChangeRow:
              !!item.isHistoricalPriceLine ||
              !!item.varianceType?.includes("price"),
            varianceType: item.varianceType || null,
            note: item.note || "",
            hasVariance:
              item.hasVariance || item.isHistoricalPriceLine || false,
            deltaQty: item.deltaQty || 0,
            baseRequiredQty: item.baseRequiredQty || 0,
            closedOutBySizeChange: item.closedOutBySizeChange || false,
            isNewSpec: item.isNewSpec || false,
            sizeChangedFrom: item.sizeChangedFrom || null,
          };

          base.isHistorical = base.isHistoricalPriceLine;
          base.isNewPriceRemaining =
            base.isPriceChangeRow && !base.isHistoricalPriceLine;

          return [base];
        });

        setPO(poData);
        setEditedTitle(poData.title || "");
        setEditedCostCode(poData.costCode?._id || poData.costCode || "");
        setEditedPrimaryVendor(
          poData.primaryVendor?._id || poData.primaryVendor || null,
        );
        setEditedNotes(poData.notes || "");
        setPoItems(mappedItems);
      } catch (err) {
        console.error("Error fetching PO data:", err);
        setAlert({
          open: true,
          type: "error",
          title: "Data Load Error",
          message: `Failed to load data: ${
            err.response?.data?.message || err.message
          }`,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [poId, id]);

  useEffect(() => {
    const fetchAvailableItems = async () => {
      if (!editing || !po || !projectId) return;

      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const res = await axios.get(
          `${API_BASE}/assemblies/project/${projectId}/materials?unassignedOnly=true`,
          { headers },
        );

        const items = res.data
          .filter((item) => {
            if (item.closedOutBySizeChange === true) return false;
            const qty =
              item.requiredQty ?? item.netRequiredQty ?? item.quantity ?? 0;
            if (qty < 0) return false;
            const remaining =
              item.remaining ??
              qty - (item.qtyPending || 0) - (item.qtyOrdered || 0);
            if (remaining <= 0) return false;
            return true;
          })
          .map((item) => ({
            ...item,
            assemblyId: item.assemblyId,
            lineItemId: item._id,
            vendorId: item.vendor?._id || item.vendor,
            vendorName: item.vendor?.companyName || "Unassigned",
            description: item.costCodeDescription || item.itemDescription || "",
            requiredQty: item.requiredQty || 0,
            amount: item.amount || 0,
            remaining:
              item.remaining ??
              item.requiredQty -
                (item.qtyPending || 0) -
                (item.qtyOrdered || 0),
            _sourceType: item._sourceType,
            _sourceBudgetTitle: item._sourceBudgetTitle,
          }));

        setAvailableItems(items);
      } catch (err) {
        console.error("Error fetching available items:", err);
      }
    };

    fetchAvailableItems();
  }, [editing, po, projectId]);

  const totals = useMemo(() => {
    const activeItems = poItems.filter((item) => !item.closedOutBySizeChange);
    const itemCount = activeItems.length;
    const estimatedTotal = activeItems.reduce((sum, item) => {
      return sum + (Number(item.amount) || 0);
    }, 0);
    return { itemCount, estimatedTotal };
  }, [poItems]);

  const hasChanges = useMemo(() => {
    if (!po) return false;
    return (
      editedTitle !== (po.title || "") ||
      editedCostCode !== (po.costCode?._id || po.costCode || "") ||
      editedPrimaryVendor !==
        (po.primaryVendor?._id || po.primaryVendor || null) ||
      editedNotes !== (po.notes || "") ||
      itemsToAdd.length > 0 ||
      itemsToRemove.length > 0
    );
  }, [
    po,
    editedTitle,
    editedCostCode,
    editedPrimaryVendor,
    editedNotes,
    itemsToAdd,
    itemsToRemove,
  ]);

  const handleAddItems = useCallback(
    (items) => {
      const newItems = items.map((item) => {
        const found = availableItems.find(
          (a) => a.assemblyId === item.assemblyId && a._id === item.lineItemId,
        );
        return {
          ...item,
          ...found,
          lineItemId: item.lineItemId,
          isNew: true,
        };
      });
      setItemsToAdd((prev) => [...prev, ...newItems]);
    },
    [availableItems],
  );

  const isMarkedForRemoval = useCallback(
    (item) => {
      return itemsToRemove.some(
        (r) => r.assemblyId === item.assemblyId && r.lineItemId === item._id,
      );
    },
    [itemsToRemove],
  );

  const filteredDisplayItems = useMemo(() => {
    const baseItems = [
      ...poItems.filter(
        (item) =>
          !itemsToRemove.some(
            (r) =>
              r.assemblyId === item.assemblyId &&
              r.lineItemId === item.lineItemId,
          ),
      ),
      ...itemsToAdd.map((item) => ({ ...item, isNew: true })),
    ];

    return baseItems.filter((item) => {
      if (
        poFilters.conditionName.length > 0 &&
        !poFilters.conditionName.includes(item.conditionName)
      )
        return false;
      if (
        poFilters.sectionNumber.length > 0 &&
        !poFilters.sectionNumber.includes(item.sectionNumber)
      )
        return false;
      if (
        poFilters.laborId.length > 0 &&
        !poFilters.laborId.includes(item.laborId)
      )
        return false;
      if (poFilters.size.length > 0 && !poFilters.size.includes(item.size))
        return false;
      if (
        poFilters.vendor.length > 0 &&
        !poFilters.vendor.includes(item.vendorName || "Unassigned")
      )
        return false;
      if (
        poFilters.bidArea.length > 0 &&
        !poFilters.bidArea.includes(item.bidArea)
      )
        return false;
      if (poFilters.descriptionSearch) {
        const blob = buildPoSearchBlob(item);
        if (!multiTokenMatch(blob, poFilters.descriptionSearch)) return false;
      }
      return true;
    });
  }, [poItems, itemsToRemove, itemsToAdd, poFilters]);

  const filteredTotals = useMemo(() => {
    const activeItems = filteredDisplayItems.filter(
      (item) => !item.closedOutBySizeChange,
    );
    const itemCount = activeItems.length;
    const estimatedTotal = activeItems.reduce((sum, item) => {
      const qty = Number(item.requiredQty ?? item.netRequiredQty ?? 0);
      const coverage = getSafeCoverage(
        item.coveragePerUnit ?? item.latestCoveragePerUnit,
      );
      const price = Number(item.price ?? item.latestPrice ?? 0);
      return sum + qty * coverage * price;
    }, 0);
    return { itemCount, estimatedTotal };
  }, [filteredDisplayItems]);

  const handleRemoveItem = useCallback((item) => {
    if ((item.qtyPending || 0) > 0 || (item.qtyOrdered || 0) > 0) {
      setAlert({
        open: true,
        type: "error",
        title: "Cannot Remove Item",
        message:
          "This item has pending or ordered quantities and cannot be removed from the PO.",
      });
      return;
    }

    setItemsToRemove((prev) => [
      ...prev,
      {
        assemblyId: item.assemblyId,
        lineItemId: item._id,
      },
    ]);
  }, []);

  const handleUndoRemove = useCallback((item) => {
    setItemsToRemove((prev) =>
      prev.filter(
        (r) =>
          !(
            r.assemblyId === item.assemblyId && r.lineItemId === item.lineItemId
          ),
      ),
    );
  }, []);

  const handleRowClick = useCallback(
    (item, e) => {
      if (e.target.closest("button") || e.target.closest("input")) return;
      if (!editing) return;
      if (item.isHistoricalPriceLine || item.closedOutBySizeChange) return;
      if (item.isNew) return;

      if (isMarkedForRemoval(item)) {
        handleUndoRemove(item);
      } else {
        handleRemoveItem(item);
      }
    },
    [editing, isMarkedForRemoval, handleUndoRemove, handleRemoveItem],
  );

  const handleRemoveFromAdd = useCallback((item) => {
    setItemsToAdd((prev) =>
      prev.filter(
        (a) =>
          !(
            a.assemblyId === item.assemblyId && a.lineItemId === item.lineItemId
          ),
      ),
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (isSaving || !hasChanges) return;
    setIsSaving(true);

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const detailsChanged =
        editedTitle !== (po.title || "") ||
        editedCostCode !== (po.costCode?._id || po.costCode || "") ||
        editedPrimaryVendor !==
          (po.primaryVendor?._id || po.primaryVendor || null) ||
        editedNotes !== (po.notes || "");

      if (detailsChanged) {
        await axios.put(
          `${API_BASE}/purchaseOrders2/${poId}`,
          {
            title: editedTitle,
            costCode: editedCostCode,
            primaryVendor: editedPrimaryVendor,
            notes: editedNotes,
          },
          { headers },
        );
      }

      if (itemsToAdd.length > 0) {
        await axios.post(
          `${API_BASE}/purchaseOrders2/${poId}/add-items`,
          {
            items: itemsToAdd.map((item) => ({
              assemblyId: item.assemblyId,
              lineItemId: item._id,
            })),
          },
          { headers },
        );
      }

      if (itemsToRemove.length > 0) {
        await axios.post(
          `${API_BASE}/purchaseOrders2/${poId}/remove-items`,
          { items: itemsToRemove },
          { headers },
        );
      }

      const [poRes, netQuantitiesRes] = await Promise.all([
        axios.get(`${API_BASE}/purchaseOrders2/${poId}`, { headers }),
        axios.get(`${API_BASE}/net-assemblies/project/${projectId}`, {
          headers,
          params: { category: "M" },
        }),
      ]);

      const poData = poRes.data;
      const netQuantitiesData = netQuantitiesRes.data.data || [];

      const getVendorName = (vendorRef) => {
        if (!vendorRef) return "Unassigned";
        const vendorId = vendorRef._id || vendorRef;
        if (typeof vendorRef === "object" && vendorRef.companyName) {
          return vendorRef.companyName;
        }
        const vendor = vendors.find((v) => v._id === vendorId);
        return vendor?.companyName || "Unassigned";
      };

      const poLinkedItems = netQuantitiesData.filter((item) => {
        const itemLinkedPO = item.linkedPO?.toString();
        return itemLinkedPO === poId;
      });

      const items = poLinkedItems.map((item) => {
        const qty = Number(item.netRequiredQty || 0);
        const coverage = getSafeCoverage(
          item.latestCoveragePerUnit || item.coveragePerUnit,
        );
        const price = Number(item.latestPrice || 0);

        return {
          ...item,
          assemblyId: item.assemblyId,
          _id: item.lineItemId,
          lineItemId: (
            item.originalLineItemId ||
            item.lineItemId ||
            ""
          ).toString(),
          originalLineItemId: item.originalLineItemId,
          vendorId: item.vendor?._id || item.vendor,
          vendorName: getVendorName(item.vendor),
          description: item.costCodeDescription || item.itemDescription || "",
          conditionName: item.conditionName || "",
          sectionNumber: item.sectionNumber || "",
          laborId: item.laborId || null,
          size: item.latestSize || item.size || "",
          bidArea: item.bidArea || "",
          requiredQty: item.netRequiredQty || 0,
          qtyPending: item.qtyPending || 0,
          qtyOrdered: item.qtyOrdered || 0,
          remaining: item.netRemaining || 0,
          amount: qty * coverage * price,
          price: price,
          coveragePerUnit: coverage,
          hasVariance: item.hasVariance || false,
          deltaQty: item.deltaQty || 0,
          baseRequiredQty: item.baseRequiredQty || 0,
          closedOutBySizeChange: item.closedOutBySizeChange || false,
          isNewSpec: item.isNewSpec || false,
          sizeChangedFrom: item.sizeChangedFrom || null,
        };
      });

      setPO(poData);
      setEditedTitle(poData.title || "");
      setEditedCostCode(poData.costCode?._id || poData.costCode || "");
      setEditedPrimaryVendor(
        poData.primaryVendor?._id || poData.primaryVendor || null,
      );
      setEditedNotes(poData.notes || "");
      setPoItems(items);

      setItemsToAdd([]);
      setItemsToRemove([]);
      setEditing(false);
      // Clear expanded rows on save
      setExpandedRows(new Set());

      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Purchase Order updated successfully",
      });
    } catch (err) {
      console.error("Error saving PO:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Save Error",
        message: `Failed to update PO: ${
          err.response?.data?.message || err.message
        }`,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    poId,
    projectId,
    editedTitle,
    editedCostCode,
    editedPrimaryVendor,
    editedNotes,
    itemsToAdd,
    itemsToRemove,
    isSaving,
    hasChanges,
    vendors,
    po,
  ]);

  const handleCancelEdit = useCallback(() => {
    if (!po) return;
    setEditedTitle(po.title || "");
    setEditedCostCode(po.costCode?._id || po.costCode || "");
    setEditedPrimaryVendor(po.primaryVendor?._id || po.primaryVendor || null);
    setEditedNotes(po.notes || "");
    setItemsToAdd([]);
    setItemsToRemove([]);
    setEditing(false);
    setExpandedRows(new Set());
    setSelectedRowKeys(new Set());
  }, [po]);

  const handleVendorSwitchConfirm = useCallback(
    async ({ newVendorId, newVendorName, varianceTitle, items }) => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        await axios.post(
          `${API_BASE}/purchaseOrders2/${poId}/switch-vendor`,
          { newVendorId, newVendorName, varianceTitle, items },
          { headers },
        );

        const [poRes, netRes] = await Promise.all([
          axios.get(`${API_BASE}/purchaseOrders2/${poId}`, { headers }),
          axios.get(`${API_BASE}/net-assemblies/project/${projectId}`, {
            headers,
            params: { category: "M", linkedPO: poId, includePrices: true },
          }),
        ]);

        const poData = poRes.data;
        const netData = netRes.data.data || [];

        const getVendorName = (vendorRef) => {
          if (!vendorRef) return "Unassigned";
          if (typeof vendorRef === "object" && vendorRef.companyName)
            return vendorRef.companyName;
          const vendorId =
            typeof vendorRef === "object" ? vendorRef._id : vendorRef;
          const vendor = vendors.find((v) => v._id === vendorId);
          return vendor?.companyName || "Unassigned";
        };

        const mappedItems = netData.flatMap((item) => {
          const qty = Number(item.netRequiredQty || 0);
          const coverage = getSafeCoverage(
            item.coveragePerUnit ?? item.latestCoveragePerUnit,
          );
          const price = Number(item.latestPrice ?? item.price ?? 0);
          const base = {
            ...item,
            assemblyId: item.assemblyId,
            sourceMONumber: extractMONumberFromTitle(
              item._sourceBudgetTitle || "",
            ),
            _id: item.lineItemId,
            lineItemId: (
              item.originalLineItemId ||
              item.lineItemId ||
              item.key ||
              ""
            ).toString(),
            originalLineItemId: item.originalLineItemId,
            vendorId: item.vendor?._id || item.vendor,
            vendorName: getVendorName(item.vendor),
            description: item.costCodeDescription || item.itemDescription || "",
            conditionName: item.conditionName || "",
            sectionNumber: item.sectionNumber || "",
            laborId: item.laborId || null,
            size: item.latestSize || item.size || "",
            bidArea: item.bidArea || "",
            requiredQty: qty,
            qtyPending: item.qtyPending || 0,
            qtyOrdered: item.qtyOrdered || 0,
            remaining: item.netRemaining || 0,
            price,
            coveragePerUnit: coverage,
            amount: qty * coverage * price,
            isHistoricalPriceLine: !!item.isHistoricalPriceLine,
            isPriceChangeRow:
              !!item.isHistoricalPriceLine ||
              !!item.varianceType?.includes("price"),
            varianceType: item.varianceType || null,
            note: item.note || "",
            hasVariance:
              item.hasVariance || item.isHistoricalPriceLine || false,
            deltaQty: item.deltaQty || 0,
            baseRequiredQty: item.baseRequiredQty || 0,
            closedOutBySizeChange: item.closedOutBySizeChange || false,
            isNewSpec: item.isNewSpec || false,
            sizeChangedFrom: item.sizeChangedFrom || null,
          };
          base.isHistorical = base.isHistoricalPriceLine;
          base.isNewPriceRemaining =
            base.isPriceChangeRow && !base.isHistoricalPriceLine;
          return [base];
        });

        setPO(poData);
        setPoItems(mappedItems);
        setSelectedRowKeys(new Set());
        setSelectedForVendorSwitch([]);
        setShowVendorSwitchModal(false);
        setExpandedRows(new Set());

        setAlert({
          open: true,
          type: "success",
          title: "Vendor Switched",
          message: `Variance "${varianceTitle}" created. ${items.length} item(s) switched to ${newVendorName}.`,
        });
      } catch (err) {
        console.error("Vendor switch error:", err);
        setAlert({
          open: true,
          type: "error",
          title: "Vendor Switch Failed",
          message: err.response?.data?.message || err.message,
        });
        throw err;
      }
    },
    [poId, projectId, vendors],
  );

  const handleOpenVendorSwitch = useCallback(() => {
    let itemsToSwitch;

    if (groupBySimilar) {
      // In grouped mode selectedRowKeys holds the group key strings produced
      // by displayRows: [description, size, vendorName].join("|||").
      // Re-build the same key for each filteredPoItems item and check membership
      // directly — no field-by-field comparison that can silently mismatch.
      itemsToSwitch = filteredPoItems.filter((item) => {
        const itemGroupKey = [
          item.description?.trim() || "—",
          item.size?.trim() || "—",
          item.vendorName || "Unassigned",
        ].join("|||");
        return selectedRowKeys.has(itemGroupKey);
      });
    } else {
      // selectedRowKeys holds item.lineItemId strings — match directly.
      itemsToSwitch = filteredPoItems.filter((item) =>
        selectedRowKeys.has(item.lineItemId),
      );
    }

    // Only items with actual remaining qty, not historical, not closed
    itemsToSwitch = itemsToSwitch.filter(
      (item) =>
        (item.remaining || 0) > 0 &&
        !item.isHistoricalPriceLine &&
        !item.closedOutBySizeChange,
    );

    if (itemsToSwitch.length === 0) {
      setAlert({
        open: true,
        type: "error",
        title: "No Eligible Items",
        message:
          "Selected items have no remaining (unordered) quantity to switch.",
      });
      return;
    }

    console.log(
      "Vendor switch payload:",
      itemsToSwitch.map((i) => ({
        description: i.description,
        lineItemId: i.lineItemId,
        remaining: i.remaining, // ← Should be 1
        qtyOrdered: i.qtyOrdered,
        requiredQty: i.requiredQty, // ← Might be 5 (don't use this!)
      })),
    );

    setSelectedForVendorSwitch(itemsToSwitch);
    setShowVendorSwitchModal(true);
  }, [selectedRowKeys, groupBySimilar, displayRows, filteredPoItems]);

  const formatCurrency = useCallback((num) => {
    return Number(num || 0)?.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  const formatNumber = useCallback((num) => {
    return Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }, []);

  const vendorOptions = useMemo(
    () => [
      { value: null, label: "None" },
      ...vendors.map((v) => ({ value: v._id, label: v.companyName })),
    ],
    [vendors],
  );

  const existingItemIds = useMemo(() => {
    const ids = new Set(poItems.map((i) => `${i.assemblyId}-${i.lineItemId}`));
    itemsToAdd.forEach((i) => ids.add(`${i.assemblyId}-${i.lineItemId}`));
    return ids;
  }, [poItems, itemsToAdd]);

  const SourceBadge = ({ source, sourceName }) => {
    const config = {
      budget: {
        bg: "bg-blue-600/90",
        border: "border-blue-400/50",
        text: "text-blue-50",
      },
      change_order: {
        bg: "bg-purple-600/90",
        border: "border-purple-400/50",
        text: "text-purple-50",
      },
    };
    const c = config[source] || config.budget;

    return (
      <div className="flex flex-col gap-0.5 min-w-[90px]">
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded border ${c.bg} ${c.border} ${c.text}`}
        >
          <span className="truncate">
            {source === "change_order" ? "Change Order" : "Budget"}
          </span>
        </span>
        <span className="text-[9px] text-gray-500 truncate" title={sourceName}>
          {sourceName}
        </span>
      </div>
    );
  };

  function AddItemsModal({
    isOpen,
    onClose,
    onAdd,
    availableItems,
    existingItemIds,
  }) {
    const [selectedItemIds, setSelectedItemIds] = useState(new Set());
    const [filters, setFilters] = useState({
      conditionName: [],
      sectionNumber: [],
      bidArea: [],
      size: [],
      uom: [],
      vendor: [],
      source: [],
      descriptionSearch: "",
    });
    const [showFilter, setShowFilter] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchInput, setSearchInput] = useState("");
    const searchTimeoutRef = useRef(null);

    const conditionRef = useRef(null);
    const sectionRef = useRef(null);
    const bidAreaRef = useRef(null);
    const sizeRef = useRef(null);
    const uomRef = useRef(null);
    const vendorRef = useRef(null);
    const descriptionRef = useRef(null);
    const sourceRef = useRef(null);

    const modalMultiSelectStyles = {
      control: (provided, state) => ({
        ...provided,
        backgroundColor: "#1f2937",
        borderColor: state.isFocused ? "#f97316" : "#4b5563",
        color: "#e5e7eb",
        boxShadow: "none",
        minHeight: "50px",
        zIndex: 100000,
        fontSize: "14px",
        "&:hover": { borderColor: "#f97316" },
      }),
      input: (provided) => ({ ...provided, color: "#e5e7eb" }),
      placeholder: (provided) => ({
        ...provided,
        color: "#9ca3af",
        fontSize: "14px",
      }),
      menuPortal: (base) => ({ ...base, zIndex: 999999 }),
      menu: (base) => ({
        ...base,
        backgroundColor: "#374151",
        borderRadius: "0.5rem",
        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
        border: "1px solid #4b5563",
        width: "300px",
        maxWidth: "300px",
        minWidth: "260px",
        zIndex: 999999,
      }),
      menuList: (base) => ({
        ...base,
        maxHeight: "380px",
        padding: "8px",
        overflowY: "auto",
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? "#4b5563" : "#374151",
        color: state.isSelected ? "#f97316" : "#e5e7eb",
        padding: "10px 12px",
        cursor: "pointer",
        fontSize: "13.5px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: "1.4",
        "&:hover": { backgroundColor: "#4b5563" },
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: "4px 8px",
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        alignItems: "flex-start",
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: "#4b5563",
        borderRadius: "6px",
        margin: 0,
        fontSize: "13px",
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: "#e5e7eb",
        padding: "3px 8px",
        paddingRight: "6px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "150px",
      }),
      multiValueRemove: (base) => ({
        ...base,
        color: "#9ca3af",
        cursor: "pointer",
        paddingLeft: "4px",
        paddingRight: "4px",
        borderRadius: "4px",
        ":hover": { backgroundColor: "#f97316", color: "white" },
      }),
    };

    useEffect(() => {
      if (isOpen) {
        setSelectedItemIds(new Set());
        setFilters({
          conditionName: [],
          sectionNumber: [],
          bidArea: [],
          size: [],
          uom: [],
          vendor: [],
          source: [],
          descriptionSearch: "",
        });
        setShowFilter(null);
        setCurrentPage(1);
        setSearchInput("");
      }
    }, [isOpen]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (!showFilter) return;

        const refs = {
          conditionName: conditionRef,
          sectionNumber: sectionRef,
          bidArea: bidAreaRef,
          size: sizeRef,
          uom: uomRef,
          vendor: vendorRef,
          source: sourceRef,
          descriptionSearch: descriptionRef,
        };
        const activeRef = refs[showFilter];

        if (activeRef?.current && activeRef.current.contains(event.target))
          return;

        const clickedInsideReactSelect =
          event.target.closest('[class*="css-"]') ||
          event.target.closest(".react-select__menu") ||
          event.target.closest(".react-select__menu-portal") ||
          event.target.closest(".react-select__option") ||
          (event.target.id && event.target.id.startsWith("react-select"));

        if (clickedInsideReactSelect) return;

        setShowFilter(null);
      };

      document.addEventListener("mousedown", handleClickOutside, true);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside, true);
    }, [showFilter]);

    useEffect(() => {
      setCurrentPage(1);
    }, [filters]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (!showPoFilter) return;

        const refs = {
          conditionName: poConditionRef,
          sectionNumber: poSectionRef,
          laborId: poLaborIdRef,
          size: poSizeRef,
          vendor: poVendorRef,
          bidArea: poBidAreaRef,
          descriptionSearch: poDescriptionRef,
        };
        const activeRef = refs[showPoFilter];

        if (activeRef?.current && activeRef.current.contains(event.target))
          return;

        const clickedInsideReactSelect =
          event.target.closest('[class*="css-"]') ||
          event.target.closest(".react-select__menu") ||
          event.target.closest(".react-select__menu-portal") ||
          event.target.closest(".react-select__option") ||
          (event.target.id && event.target.id.startsWith("react-select"));

        if (clickedInsideReactSelect) return;

        setShowPoFilter(null);
      };

      document.addEventListener("mousedown", handleClickOutside, true);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside, true);
    }, [showPoFilter]);

    const unassignedItems = useMemo(() => {
      return availableItems.filter((item) => {
        if (existingItemIds.has(`${item.assemblyId}-${item._id}`)) return false;
        if (item.closedOutBySizeChange === true) return false;
        const qty = item.requiredQty ?? item.quantity ?? 0;
        if (qty < 0) return false;
        const remaining = item.remaining ?? qty;
        if (remaining <= 0) return false;
        return true;
      });
    }, [availableItems, existingItemIds]);

    const filteredItems = useMemo(() => {
      return unassignedItems.filter((item) => {
        if (
          filters.conditionName.length > 0 &&
          !filters.conditionName.includes(item.conditionName)
        )
          return false;
        if (
          filters.sectionNumber.length > 0 &&
          !filters.sectionNumber.includes(item.sectionNumber)
        )
          return false;
        if (
          filters.bidArea.length > 0 &&
          !filters.bidArea.includes(item.bidArea)
        )
          return false;
        if (filters.size.length > 0 && !filters.size.includes(item.size))
          return false;
        if (filters.uom.length > 0 && !filters.uom.includes(item.uom))
          return false;
        if (
          filters.vendor.length > 0 &&
          !filters.vendor.includes(item.vendor?.companyName || "Unassigned")
        )
          return false;
        if (filters.source.length > 0) {
          const itemSource =
            item._sourceType === "change_order" ? "Change Order" : "Budget";
          if (!filters.source.includes(itemSource)) return false;
        }
        if (filters.descriptionSearch) {
          const blob = buildPoSearchBlob({
            description: item.costCodeDescription || item.itemDescription || "",
            conditionName: item.conditionName || "",
            sectionNumber: item.sectionNumber || "",
            size: item.size || "",
            vendorName: item.vendor?.companyName || "",
            bidArea: item.bidArea || "",
          });
          if (!multiTokenMatch(blob, filters.descriptionSearch)) return false;
        }
        return true;
      });
    }, [unassignedItems, filters]);

    const naturalCompare = (a, b) => {
      return a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    };

    const sortedFilteredItems = useMemo(() => {
      if (filteredItems.length === 0) return [];

      return [...filteredItems].sort((a, b) => {
        const assemblyA = (a.conditionName || "Uncategorized").trim();
        const assemblyB = (b.conditionName || "Uncategorized").trim();

        if (assemblyA !== assemblyB) {
          return assemblyA.localeCompare(assemblyB);
        }

        const isChangeOrderA = a._sourceType === "change_order";
        const isChangeOrderB = b._sourceType === "change_order";

        if (isChangeOrderA !== isChangeOrderB) {
          return isChangeOrderA ? 1 : -1;
        }

        const sectionA = a.sectionNumber || "";
        const sectionB = b.sectionNumber || "";
        if (sectionA !== sectionB) {
          return sectionA.localeCompare(sectionB);
        }

        const descA = (
          a.costCodeDescription ||
          a.itemDescription ||
          ""
        ).toLowerCase();
        const descB = (
          b.costCodeDescription ||
          b.itemDescription ||
          ""
        ).toLowerCase();
        if (descA !== descB) {
          return descA.localeCompare(descB);
        }

        return (a.size || "").localeCompare(b.size || "");
      });
    }, [filteredItems]);

    const sortedItems = useMemo(() => {
      const start = (currentPage - 1) * 100;
      return sortedFilteredItems.slice(start, start + 100);
    }, [sortedFilteredItems, currentPage]);

    const selectedItems = useMemo(() => {
      return filteredItems.filter((item) => selectedItemIds.has(item._id));
    }, [filteredItems, selectedItemIds]);

    const modalTotals = useMemo(() => {
      return selectedItems.reduce(
        (acc, item) => ({
          count: acc.count + 1,
          qty: acc.qty + (item.requiredQty || item.quantity || 0),
          amount: acc.amount + (item.amount || item.materialTotal || 0),
        }),
        { count: 0, qty: 0, amount: 0 },
      );
    }, [selectedItems]);

    const filterOptions = useMemo(() => {
      return {
        conditionName: [
          ...new Set(
            unassignedItems.map((i) => i.conditionName).filter(Boolean),
          ),
        ].sort(naturalCompare),
        sectionNumber: [
          ...new Set(
            unassignedItems.map((i) => i.sectionNumber).filter(Boolean),
          ),
        ].sort(naturalCompare),
        bidArea: [
          ...new Set(unassignedItems.map((i) => i.bidArea).filter(Boolean)),
        ].sort(),
        size: [
          ...new Set(unassignedItems.map((i) => i.size).filter(Boolean)),
        ].sort(naturalCompare),
        uom: [
          ...new Set(unassignedItems.map((i) => i.uom).filter(Boolean)),
        ].sort(),
        vendor: [
          ...new Set(
            unassignedItems
              .map((i) => i.vendor?.companyName || "Unassigned")
              .filter(Boolean),
          ),
        ].sort(),
        source: ["Budget", "Change Order"],
      };
    }, [unassignedItems]);

    const hasActiveFilters = Object.values(filters).some((f) =>
      Array.isArray(f) ? f.length > 0 : f,
    );

    const toggleFilter = (field) =>
      setShowFilter((prev) => (prev === field ? null : field));

    const handleFilterChange = (type, value) => {
      if (type === "descriptionSearch") {
        setFilters((prev) => ({ ...prev, descriptionSearch: value }));
      } else {
        setFilters((prev) => ({ ...prev, [type]: value || [] }));
      }
    };

    const handleDescriptionSearchChange = (e) => {
      const value = e.target.value;
      setSearchInput(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(
        () => handleFilterChange("descriptionSearch", value),
        300,
      );
    };

    const clearAllFilters = () => {
      setFilters({
        conditionName: [],
        sectionNumber: [],
        bidArea: [],
        size: [],
        uom: [],
        source: [],
        vendor: [],
        descriptionSearch: "",
      });
      setSearchInput("");
      setSelectedItemIds(new Set());
      setCurrentPage(1);
    };

    const handleSelectAllFiltered = () => {
      if (selectedItemIds.size === filteredItems.length) {
        setSelectedItemIds(new Set());
      } else {
        setSelectedItemIds(new Set(filteredItems.map((i) => i._id)));
      }
    };

    const allFilteredSelected =
      selectedItemIds.size === filteredItems.length && filteredItems.length > 0;

    const handleItemSelect = (item) => {
      setSelectedItemIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(item._id)) newSet.delete(item._id);
        else newSet.add(item._id);
        return newSet;
      });
    };

    const formatCurrencyModal = (num) =>
      `$${Number(num || 0)?.toLocaleString("en-US", {
        minimumFractionDigits: 2,
      })}`;

    const formatNumberModal = (num) =>
      Number(num || 0).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

    const handleAddSelected = () => {
      const itemsToAddList = selectedItems.map((item) => ({
        assemblyId: item.assemblyId,
        lineItemId: item._id,
      }));
      onAdd(itemsToAddList);
      onClose();
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
          <div
            className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75"
            onClick={onClose}
          />
          <div className="relative inline-block w-full max-w-7xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-gray-800 rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-white">
                  Add Material Items
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  {modalTotals.count} items selected •{" "}
                  {formatCurrencyModal(modalTotals.amount)}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="bg-gray-900 rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-4 text-xl">
                  <span className="text-gray-400">
                    <span className="text-white font-semibold">
                      {filteredItems.length?.toLocaleString()}
                    </span>
                    {filteredItems.length !== unassignedItems.length && (
                      <span className="text-gray-500">
                        {" "}
                        / {unassignedItems.length?.toLocaleString()}
                      </span>
                    )}{" "}
                    available
                  </span>
                  <span className="text-gray-600">|</span>
                  <span className="text-orange-400 font-medium">
                    {formatCurrencyModal(filteredTotals.amount)}
                  </span>
                  <span className="text-gray-600">|</span>
                  <span className="text-blue-400">
                    Qty: {formatNumberModal(filteredTotals.qty)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSelectAllFiltered}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-orange-500 rounded hover:bg-orange-600"
                  >
                    {allFilteredSelected ? "Deselect All" : "Select All"}
                  </button>
                  <span className="text-gray-400 text-sm">
                    {selectedItems.length} selected
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <input
                    type="text"
                    value={searchInput}
                    onChange={handleDescriptionSearchChange}
                    placeholder="Search..."
                    className="w-40 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput("");
                        handleFilterChange("descriptionSearch", "");
                      }}
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      ×
                    </button>
                  )}
                </div>
                <span className="text-gray-600">|</span>

                {filters.conditionName.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-teal-600/80 text-white rounded">
                    Assembly:{" "}
                    {filters.conditionName.length === 1
                      ? filters.conditionName[0].substring(0, 15) +
                        (filters.conditionName[0].length > 15 ? "…" : "")
                      : `${filters.conditionName.length} selected`}
                    <button
                      type="button"
                      onClick={() => handleFilterChange("conditionName", [])}
                      className="hover:text-teal-200"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filters.sectionNumber.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-blue-600/80 text-white rounded">
                    Section:{" "}
                    {filters.sectionNumber.length === 1
                      ? filters.sectionNumber[0]
                      : `${filters.sectionNumber.length} selected`}
                    <button
                      type="button"
                      onClick={() => handleFilterChange("sectionNumber", [])}
                      className="hover:text-blue-200"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filters.bidArea.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-green-600/80 text-white rounded">
                    Area:{" "}
                    {filters.bidArea.length === 1
                      ? filters.bidArea[0]
                      : `${filters.bidArea.length} selected`}
                    <button
                      type="button"
                      onClick={() => handleFilterChange("bidArea", [])}
                      className="hover:text-green-200"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filters.size.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-cyan-600/80 text-white rounded">
                    Size:{" "}
                    {filters.size.length === 1
                      ? filters.size[0]
                      : `${filters.size.length} selected`}
                    <button
                      type="button"
                      onClick={() => handleFilterChange("size", [])}
                      className="hover:text-cyan-200"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filters.uom.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-purple-600/80 text-white rounded">
                    UOM:{" "}
                    {filters.uom.length === 1
                      ? filters.uom[0]
                      : `${filters.uom.length} selected`}
                    <button
                      type="button"
                      onClick={() => handleFilterChange("uom", [])}
                      className="hover:text-purple-200"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filters.vendor.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-indigo-600/80 text-white rounded">
                    Vendor:{" "}
                    {filters.vendor.length === 1
                      ? filters.vendor[0].substring(0, 12) +
                        (filters.vendor[0].length > 12 ? "…" : "")
                      : `${filters.vendor.length} selected`}
                    <button
                      type="button"
                      onClick={() => handleFilterChange("vendor", [])}
                      className="hover:text-indigo-200"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filters.source.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-amber-600/80 text-white rounded">
                    Source:{" "}
                    {filters.source.length === 1
                      ? filters.source[0]
                      : `${filters.source.length} selected`}
                    <button
                      type="button"
                      onClick={() => handleFilterChange("source", [])}
                      className="hover:text-amber-200"
                    >
                      ×
                    </button>
                  </span>
                )}
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="px-1.5 py-0.5 text-[11px] font-medium text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-700 max-h-[50vh]">
              <table className="w-full text-[13px]">
                <thead className="bg-gray-800/95 sticky top-0 z-10">
                  <tr className="border-b border-gray-700">
                    <th className="w-8 px-1 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={
                          filteredItems.length > 0 &&
                          filteredItems.every((i) => selectedItemIds.has(i._id))
                        }
                        onChange={() => {
                          if (
                            filteredItems.every((i) =>
                              selectedItemIds.has(i._id),
                            )
                          ) {
                            setSelectedItemIds((prev) => {
                              const next = new Set(prev);
                              filteredItems.forEach((i) => next.delete(i._id));
                              return next;
                            });
                          } else {
                            setSelectedItemIds((prev) => {
                              const next = new Set(prev);
                              filteredItems.forEach((i) => next.add(i._id));
                              return next;
                            });
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-500 text-orange-500 focus:ring-orange-500"
                      />
                    </th>
                    <th
                      className={`w-40 px-2 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.source.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("source")}
                    >
                      <span className="flex items-center gap-0.5">
                        Source ↓{" "}
                        {filters.source.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.source.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "source" && (
                        <div
                          ref={sourceRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{ minWidth: "200px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={filterOptions.source.map((s) => ({
                                value: s,
                                label: s,
                              }))}
                              value={filterOptions.source
                                .map((s) => ({ value: s, label: s }))
                                .filter((o) =>
                                  filters.source.includes(o.value),
                                )}
                              onChange={(opts) =>
                                handleFilterChange(
                                  "source",
                                  opts ? opts.map((o) => o.value) : [],
                                )
                              }
                              menuShouldBlockScroll={true}
                              menuPortalTarget={document.body}
                              styles={modalMultiSelectStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th
                      className={`w-64 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.conditionName.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("conditionName")}
                    >
                      <span className="flex items-center gap-0.5">
                        Assembly ↓{" "}
                        {filters.conditionName.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.conditionName.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "conditionName" && (
                        <div
                          ref={conditionRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{ minWidth: "300px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={filterOptions.conditionName.map((c) => ({
                                value: c,
                                label: c,
                              }))}
                              value={filterOptions.conditionName
                                .map((c) => ({ value: c, label: c }))
                                .filter((o) =>
                                  filters.conditionName.includes(o.value),
                                )}
                              onChange={(opts) =>
                                handleFilterChange(
                                  "conditionName",
                                  opts ? opts.map((o) => o.value) : [],
                                )
                              }
                              menuShouldBlockScroll={true}
                              menuPortalTarget={document.body}
                              styles={modalMultiSelectStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th
                      className={`w-24 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.sectionNumber.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("sectionNumber")}
                    >
                      <span className="flex items-center gap-0.5">
                        Section ↓{" "}
                        {filters.sectionNumber.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.sectionNumber.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "sectionNumber" && (
                        <div
                          ref={sectionRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{ minWidth: "200px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              options={filterOptions.sectionNumber.map((s) => ({
                                value: s,
                                label: s,
                              }))}
                              value={filterOptions.sectionNumber
                                .map((s) => ({ value: s, label: s }))
                                .filter((o) =>
                                  filters.sectionNumber.includes(o.value),
                                )}
                              onChange={(opts) =>
                                handleFilterChange(
                                  "sectionNumber",
                                  opts ? opts.map((o) => o.value) : [],
                                )
                              }
                              menuShouldBlockScroll={true}
                              menuPortalTarget={document.body}
                              styles={modalMultiSelectStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th
                      className={`min-w-[180px] px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.descriptionSearch
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("descriptionSearch")}
                    >
                      <span className="flex items-center gap-0.5">
                        Description
                      </span>
                      {showFilter === "descriptionSearch" && (
                        <div
                          ref={descriptionRef}
                          className="absolute z-20 mt-1 bg-gray-700 border border-gray-600 rounded shadow-xl w-56"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-1.5">
                            <input
                              type="text"
                              value={searchInput}
                              onChange={handleDescriptionSearchChange}
                              placeholder="Type to search..."
                              className="w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-[13px] py-1 px-2 focus:outline-none focus:border-orange-500"
                              autoFocus
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th
                      className={`w-24 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.size.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("size")}
                    >
                      <span className="flex items-center gap-0.5">
                        Size ↓{" "}
                        {filters.size.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.size.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "size" && (
                        <div
                          ref={sizeRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{ minWidth: "180px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              options={filterOptions.size.map((s) => ({
                                value: s,
                                label: s,
                              }))}
                              value={filterOptions.size
                                .map((s) => ({ value: s, label: s }))
                                .filter((o) => filters.size.includes(o.value))}
                              onChange={(opts) =>
                                handleFilterChange(
                                  "size",
                                  opts ? opts.map((o) => o.value) : [],
                                )
                              }
                              menuShouldBlockScroll={true}
                              menuPortalTarget={document.body}
                              styles={modalMultiSelectStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th
                      className={`w-20 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.bidArea.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("bidArea")}
                    >
                      <span className="flex items-center gap-0.5">
                        Area ↓{" "}
                        {filters.bidArea.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.bidArea.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "bidArea" && (
                        <div
                          ref={bidAreaRef}
                          className="absolute left-0 top-full mt-1 z-[999999]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            className="bg-gray-700 border border-gray-500 rounded-lg shadow-2xl overflow-hidden"
                            style={{ width: "280px" }}
                          >
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              options={filterOptions.bidArea.map((b) => ({
                                value: b,
                                label: b,
                              }))}
                              value={filterOptions.bidArea
                                .map((b) => ({ value: b, label: b }))
                                .filter((o) =>
                                  filters.bidArea.includes(o.value),
                                )}
                              onChange={(opts) =>
                                handleFilterChange(
                                  "bidArea",
                                  opts ? opts.map((o) => o.value) : [],
                                )
                              }
                              menuShouldBlockScroll={true}
                              menuPortalTarget={document.body}
                              styles={modalMultiSelectStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Req Qty
                    </th>
                    <th
                      className={`w-16 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.uom.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("uom")}
                    >
                      <span className="flex items-center gap-0.5">
                        UOM ↓{" "}
                        {filters.uom.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.uom.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "uom" && (
                        <div
                          ref={uomRef}
                          className="absolute left-0 top-full mt-1 z-50 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{ minWidth: "180px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              options={filterOptions.uom.map((u) => ({
                                value: u,
                                label: u,
                              }))}
                              value={filterOptions.uom
                                .map((u) => ({ value: u, label: u }))
                                .filter((o) => filters.uom.includes(o.value))}
                              onChange={(opts) =>
                                handleFilterChange(
                                  "uom",
                                  opts ? opts.map((o) => o.value) : [],
                                )
                              }
                              menuShouldBlockScroll={true}
                              menuPortalTarget={document.body}
                              styles={modalMultiSelectStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th
                      className={`w-32 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.vendor.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("vendor")}
                    >
                      <span className="flex items-center gap-0.5">
                        Vendor ↓{" "}
                        {filters.vendor.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.vendor.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "vendor" && (
                        <div
                          ref={vendorRef}
                          className="absolute left-0 top-full mt-1 z-50 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{ minWidth: "280px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              options={filterOptions.vendor.map((v) => ({
                                value: v,
                                label: v,
                              }))}
                              value={filterOptions.vendor
                                .map((v) => ({ value: v, label: v }))
                                .filter((o) =>
                                  filters.vendor.includes(o.value),
                                )}
                              onChange={(opts) =>
                                handleFilterChange(
                                  "vendor",
                                  opts ? opts.map((o) => o.value) : [],
                                )
                              }
                              menuShouldBlockScroll={true}
                              menuPortalTarget={document.body}
                              styles={modalMultiSelectStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase rounded-tr-lg">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {sortedItems.length === 0 ? (
                    <tr>
                      <td
                        colSpan="11"
                        className="px-4 py-12 text-center text-gray-400"
                      >
                        {filteredItems.length === 0
                          ? "No items match current filters"
                          : "No available items"}
                      </td>
                    </tr>
                  ) : (
                    sortedItems.map((item, idx) => {
                      const isSelected = selectedItemIds.has(item._id);

                      return (
                        <tr
                          key={item._id}
                          className={`hover:bg-gray-700/50 cursor-pointer transition-all ${
                            idx % 2 === 0 ? "bg-gray-800/30" : "bg-gray-400/5"
                          } ${
                            isSelected
                              ? "ring-2 ring-orange-500 bg-orange-900/30"
                              : ""
                          }`}
                          onClick={() => handleItemSelect(item)}
                        >
                          <td
                            className="px-2 py-2 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleItemSelect(item)}
                              className="h-4 w-4 text-orange-400 rounded border-gray-500"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <SourceBadge
                              source={item._sourceType || "budget"}
                              sourceName={
                                item._sourceBudgetTitle || "Original Budget"
                              }
                            />
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-300 font-medium">
                            {item.conditionName || "-"}
                          </td>
                          <td className="px-2 py-2 text-sm text-white">
                            {item.sectionNumber || "-"}
                          </td>
                          <td
                            className="px-2 py-2 text-sm text-gray-200 max-w-[180px] truncate"
                            title={
                              item.costCodeDescription || item.itemDescription
                            }
                          >
                            {item.costCodeDescription ||
                              item.itemDescription ||
                              "No Description"}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-100">
                            {item.size || "-"}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-200">
                            {item.bidArea || "-"}
                          </td>
                          <td className="px-2 py-2 text-sm text-right text-white font-medium">
                            {formatNumberModal(
                              item.requiredQty || item.quantity || 0,
                            )}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-200">
                            {item.uom || "-"}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-200">
                            {item.vendor?.companyName || "Unassigned"}
                          </td>
                          <td className="px-2 py-2 text-sm text-right text-white">
                            {formatCurrencyModal(
                              item.amount || item.materialTotal,
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {Math.ceil(filteredItems.length / 100) > 1 && (
              <div className="flex items-center justify-center gap-2 py-4 mt-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-[13px] bg-gray-700 text-gray-400 rounded disabled:opacity-30 hover:bg-gray-600"
                >
                  ««
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600"
                >
                  ‹ Prev
                </button>
                <span className="text-[13px] text-white">
                  Page {currentPage} of {Math.ceil(filteredItems.length / 100)}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.min(Math.ceil(filteredItems.length / 100), p + 1),
                    )
                  }
                  disabled={
                    currentPage === Math.ceil(filteredItems.length / 100)
                  }
                  className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600"
                >
                  Next ›
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage(Math.ceil(filteredItems.length / 100))
                  }
                  disabled={
                    currentPage === Math.ceil(filteredItems.length / 100)
                  }
                  className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600"
                >
                  »»
                </button>
              </div>
            )}

            {modalTotals.count > 0 && (
              <div className="mt-4 p-4 bg-gray-900 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-gray-200">
                  <div>
                    <span className="text-gray-400">Items:</span>{" "}
                    {modalTotals.count}
                  </div>
                  <div>
                    <span className="text-gray-400">Total Qty:</span>{" "}
                    {formatNumberModal(modalTotals.qty)}
                  </div>
                  <div>
                    <span className="text-gray-400">Estimated Total:</span>{" "}
                    <span className="text-orange-400 font-semibold">
                      {formatCurrencyModal(modalTotals.amount)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddSelected}
                disabled={modalTotals.count === 0}
                className={`px-4 py-2 text-sm text-white rounded-md ${
                  modalTotals.count === 0
                    ? "bg-orange-300 cursor-not-allowed"
                    : "bg-orange-500 hover:bg-orange-600"
                }`}
              >
                Add {modalTotals.count} Item
                {modalTotals.count !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const costCodeOptions = useMemo(
    () =>
      costCodes.map((cc) => ({
        value: cc._id,
        label: cc.code || "Unnamed Cost Code",
      })),
    [costCodes],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-200">Loading...</div>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-200">Purchase Order not found</div>
      </div>
    );
  }

  const PriceChangeBadge = ({ item }) => {
    if (!item.isPriceChangeRow && !item.isHistoricalPriceLine) return null;

    if (item.isHistoricalPriceLine) {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold rounded bg-gray-600/80 text-gray-200 border border-gray-500/50">
          ORDERED @ OLD PRICE
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold rounded bg-purple-600/80 text-purple-100 border border-purple-400/50">
        NEW PRICE
      </span>
    );
  };

  // Sortable numeric header helper
  const SortableNumericHeader = ({ label, sortKey, className = "" }) => (
    <th
      className={`px-3 py-3 text-right text-xs font-medium uppercase cursor-pointer select-none hover:text-orange-300 transition-colors ${
        sortConfig.key === sortKey ? "text-orange-400" : "text-gray-400"
      } ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <span className="flex items-center justify-end gap-1">
        {label}
        <SortIcon column={sortKey} sortConfig={sortConfig} />
      </span>
    </th>
  );

  const hasActiveTableFilters =
    Object.values(poTableFilters).some((v) =>
      Array.isArray(v) ? v.length > 0 : !!v,
    ) || quickFilter !== null;

  // Total column count for expand row colspan
  const totalColCount = editing ? 16 : 16;

  return (
    <div className="max-w-full mx-auto px-4 py-6">
      <AlertDialog
        type={alert.type}
        title={alert.title}
        message={alert.message}
        open={alert.open}
        onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
        onConfirm={() => setAlert((prev) => ({ ...prev, open: false }))}
        confirmText="OK"
        cancelText="Close"
      />

      <AddItemsModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddItems}
        availableItems={availableItems}
        existingItemIds={existingItemIds}
      />

      {/* Header */}
      <div className="mb-6 shadow-xl bg-gray-700 rounded-lg p-6 border border-gray-600">
        <div className="flex justify-between items-center">
          <div>
            {editing ? (
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="text-2xl font-semibold text-gray-200 bg-gray-600 border-gray-500 rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Enter PO title"
              />
            ) : (
              <h1 className="text-2xl font-semibold text-white">
                {po.title} (PO# {po.poNumber})
              </h1>
            )}
            <p className="text-sm text-gray-400 mt-1">
              {totals.itemCount} items • Estimated Total: $
              {formatCurrency(totals.estimatedTotal)}
              <span className="ml-2 text-blue-400">(Net quantities shown)</span>
            </p>
          </div>
          <button
            onClick={() =>
              navigate(`/Projects/${id}`, {
                state: { initialTab: "Purchase Orders" },
              })
            }
            className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            Back
          </button>
        </div>
      </div>

      {/* PO Details (edit mode) */}
      {editing && (
        <div className="mb-6 bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
          <h2 className="text-lg font-semibold text-white mb-4">PO Details</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Cost Code
              </label>
              <Select
                options={costCodeOptions}
                value={
                  costCodeOptions.find((o) => o.value === editedCostCode) ||
                  null
                }
                onChange={(opt) => setEditedCostCode(opt?.value || "")}
                placeholder="Select Cost Code..."
                className="mt-1"
                styles={customStyles}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Primary Vendor
              </label>
              <Select
                options={vendorOptions}
                value={
                  vendorOptions.find((o) => o.value === editedPrimaryVendor) ||
                  vendorOptions[0]
                }
                onChange={(opt) => setEditedPrimaryVendor(opt?.value || null)}
                placeholder="Select Vendor..."
                className="mt-1"
                styles={customStyles}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Notes
              </label>
              <input
                type="text"
                value={editedNotes}
                onChange={(e) => setEditedNotes(e.target.value)}
                placeholder="Optional notes..."
                className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Line Items */}
      <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">
              Purchase Order Items
              <span className="ml-2 text-sm font-normal text-gray-400">
                (Quantities include variance/change order adjustments)
              </span>
            </h2>
            {!editing && (
              <p className="text-xs text-gray-500 mt-0.5">
                Click any row to see which material orders it was ordered from
              </p>
            )}
          </div>
          <div className="flex items-center gap-6">
            <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
              <span className="text-gray-300">Group similar items</span>
              <div
                className={`relative inline-flex h-5 w-10 cursor-pointer items-center rounded-full transition-colors ${
                  groupBySimilar ? "bg-orange-600" : "bg-gray-600"
                }`}
                onClick={() => setGroupBySimilar(!groupBySimilar)}
              >
                <div
                  className={`absolute w-4 h-4 rounded-full bg-white transition-all ${
                    groupBySimilar ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </div>
            </label>

            {!editing && (
              <button
                onClick={() => {
                  setEditing(true);
                  setSelectedRowKeys(new Set());
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500"
              >
                Edit PO
              </button>
            )}
            {editing && (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges}
                  className={`px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200 ${
                    isSaving || !hasChanges
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {isSaving ? "Saving..." : "Save PO"}
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(true)}
                  className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center gap-2"
                >
                  <span className="text-lg">+</span> Add Material Items
                </button>
              </>
            )}
          </div>
        </div>

        {/* Filter bar */}
        <div className="bg-gray-800 rounded-lg p-3 mb-4 space-y-3">
          {/* Row 1: search + clear */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative w-64">
              <input
                type="text"
                value={poTableFilters.descriptionSearch}
                onChange={(e) =>
                  setPoTableFilters((prev) => ({
                    ...prev,
                    descriptionSearch: e.target.value,
                  }))
                }
                placeholder="Search description, size, assembly..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 text-sm"
              />
              {poTableFilters.descriptionSearch && (
                <button
                  onClick={() =>
                    setPoTableFilters((prev) => ({
                      ...prev,
                      descriptionSearch: "",
                    }))
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  ×
                </button>
              )}
            </div>

            {hasActiveTableFilters && (
              <button
                onClick={() => {
                  setPoTableFilters({
                    conditionName: [],
                    sectionNumber: [],
                    size: [],
                    vendor: [],
                    bidArea: [],
                    descriptionSearch: "",
                  });
                  setQuickFilter(null);
                  setSortConfig({ key: null, dir: "asc" });
                }}
                className="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 bg-gray-700 rounded-md hover:bg-red-900/30"
              >
                Clear All
              </button>
            )}

            {!editing && selectedRowKeys.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">
                  {selectedRowKeys.size} selected
                </span>
                <button
                  onClick={handleOpenVendorSwitch}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors shadow-sm"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                  Switch Vendor
                </button>
                <button
                  onClick={() => setSelectedRowKeys(new Set())}
                  className="text-xs text-gray-500 hover:text-gray-300 px-2 py-1 rounded hover:bg-gray-700 transition-colors"
                >
                  Clear
                </button>
              </div>
            )}
            {sortConfig.key && (
              <button
                onClick={() => setSortConfig({ key: null, dir: "asc" })}
                className="px-2 py-1 text-xs text-orange-400 hover:text-orange-300 bg-gray-700 rounded border border-orange-500/40 hover:bg-orange-900/20 flex items-center gap-1"
              >
                <span>
                  Sort:{" "}
                  {sortConfig.key === "requiredQty"
                    ? "Net Qty"
                    : sortConfig.key === "qtyPending"
                      ? "Pending"
                      : sortConfig.key === "qtyOrdered"
                        ? "Ordered"
                        : sortConfig.key === "remaining"
                          ? "Remaining"
                          : sortConfig.key === "price"
                            ? "Unit Cost"
                            : "Amount"}{" "}
                  {sortConfig.dir === "asc" ? "↑" : "↓"}
                </span>
                <span className="text-gray-500 hover:text-red-400">×</span>
              </button>
            )}

            <div className="ml-auto text-sm text-gray-300">
              Showing{" "}
              <strong>
                {groupBySimilar ? displayRows.length : filteredPoItems.length}
              </strong>
              {" / "}
              <span className="text-gray-500">
                {quickFilterCounts.total}
              </span>{" "}
              {groupBySimilar ? "groups" : "items"}
            </div>
          </div>

          {/* Row 2: Quick filter badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wide mr-1">
              Quick filter:
            </span>

            <button
              onClick={() =>
                setQuickFilter(quickFilter === "available" ? null : "available")
              }
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                quickFilter === "available"
                  ? "bg-green-500 border-green-400 text-white shadow-md shadow-green-900/40"
                  : "bg-green-900/20 border-green-700/50 text-green-400 hover:bg-green-900/40 hover:border-green-600"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${quickFilter === "available" ? "bg-white" : "bg-green-400"}`}
              />
              Available
              <span
                className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${quickFilter === "available" ? "bg-green-400 text-green-900" : "bg-green-900/50 text-green-300"}`}
              >
                {quickFilterCounts.available}
              </span>
            </button>

            <button
              onClick={() =>
                setQuickFilter(quickFilter === "pending" ? null : "pending")
              }
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                quickFilter === "pending"
                  ? "bg-yellow-500 border-yellow-400 text-gray-900 shadow-md shadow-yellow-900/40"
                  : "bg-yellow-900/20 border-yellow-700/50 text-yellow-400 hover:bg-yellow-900/40 hover:border-yellow-600"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${quickFilter === "pending" ? "bg-gray-900" : "bg-yellow-400"}`}
              />
              Has Pending
              <span
                className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${quickFilter === "pending" ? "bg-yellow-400 text-yellow-900" : "bg-yellow-900/50 text-yellow-300"}`}
              >
                {quickFilterCounts.pending}
              </span>
            </button>

            <button
              onClick={() =>
                setQuickFilter(
                  quickFilter === "fullyOrdered" ? null : "fullyOrdered",
                )
              }
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                quickFilter === "fullyOrdered"
                  ? "bg-blue-500 border-blue-400 text-white shadow-md shadow-blue-900/40"
                  : "bg-blue-900/20 border-blue-700/50 text-blue-400 hover:bg-blue-900/40 hover:border-blue-600"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${quickFilter === "fullyOrdered" ? "bg-white" : "bg-blue-400"}`}
              />
              Fully Ordered
              <span
                className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${quickFilter === "fullyOrdered" ? "bg-blue-400 text-blue-900" : "bg-blue-900/50 text-blue-300"}`}
              >
                {quickFilterCounts.fullyOrdered}
              </span>
            </button>

            <button
              onClick={() =>
                setQuickFilter(
                  quickFilter === "zeroRemaining" ? null : "zeroRemaining",
                )
              }
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                quickFilter === "zeroRemaining"
                  ? "bg-gray-400 border-gray-300 text-gray-900 shadow-md"
                  : "bg-gray-700/50 border-gray-600 text-gray-400 hover:bg-gray-700 hover:border-gray-500"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${quickFilter === "zeroRemaining" ? "bg-gray-900" : "bg-gray-400"}`}
              />
              0 Remaining
              <span
                className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${quickFilter === "zeroRemaining" ? "bg-gray-300 text-gray-800" : "bg-gray-700 text-gray-400"}`}
              >
                {quickFilterCounts.zeroRemaining}
              </span>
            </button>
          </div>
        </div>

        {/* Items Table */}
        <div className="rounded-lg shadow-xl overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
            <thead className="bg-gray-800 sticky top-0 z-10">
              <tr>
                {/* Expand indicator column */}
                {!editing && <th className="w-6 px-1 py-3" />}
                {!editing && (
                  <th className="w-8 px-2 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={
                        filteredPoItems.filter(
                          (i) =>
                            (i.remaining || 0) > 0 &&
                            !i.isHistoricalPriceLine &&
                            !i.closedOutBySizeChange,
                        ).length > 0 &&
                        filteredPoItems
                          .filter(
                            (i) =>
                              (i.remaining || 0) > 0 &&
                              !i.isHistoricalPriceLine &&
                              !i.closedOutBySizeChange,
                          )
                          .every((i) => selectedRowKeys.has(i.lineItemId))
                      }
                      onChange={(e) => {
                        const eligible = filteredPoItems.filter(
                          (i) =>
                            (i.remaining || 0) > 0 &&
                            !i.isHistoricalPriceLine &&
                            !i.closedOutBySizeChange,
                        );
                        if (e.target.checked) {
                          setSelectedRowKeys(
                            new Set(eligible.map((i) => i.lineItemId)),
                          );
                        } else {
                          setSelectedRowKeys(new Set());
                        }
                      }}
                      className="h-3.5 w-3.5 rounded border-gray-500 text-orange-500 focus:ring-orange-500 cursor-pointer"
                    />
                  </th>
                )}

                {/* 1. Condition / Assembly */}
                <th
                  className={`px-3 py-3 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 uppercase ${
                    poTableFilters.conditionName.length > 0
                      ? "text-orange-400"
                      : "text-gray-400"
                  }`}
                  onClick={() =>
                    setShowPoTableFilter((prev) =>
                      prev === "conditionName" ? null : "conditionName",
                    )
                  }
                >
                  <span className="flex items-center gap-0.5">
                    Condition ↓
                    {poTableFilters.conditionName.length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                        {poTableFilters.conditionName.length}
                      </span>
                    )}
                  </span>
                  {showPoTableFilter === "conditionName" && (
                    <div
                      ref={poTableConditionRef}
                      className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                      style={{ minWidth: "300px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-3">
                        <Select
                          isMulti
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          options={poTableFilterOptions.conditionName.map(
                            (v) => ({ value: v, label: v }),
                          )}
                          value={poTableFilterOptions.conditionName
                            .map((v) => ({ value: v, label: v }))
                            .filter((opt) =>
                              poTableFilters.conditionName.includes(opt.value),
                            )}
                          onChange={(opts) =>
                            setPoTableFilters((prev) => ({
                              ...prev,
                              conditionName: opts
                                ? opts.map((o) => o.value)
                                : [],
                            }))
                          }
                          menuPortalTarget={document.body}
                          styles={multiSelectFilterStyles}
                        />
                      </div>
                    </div>
                  )}
                </th>

                {/* 2. Section */}
                <th
                  className={`px-3 py-3 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 uppercase ${
                    poTableFilters.sectionNumber.length > 0
                      ? "text-orange-400"
                      : "text-gray-400"
                  }`}
                  onClick={() =>
                    setShowPoTableFilter((prev) =>
                      prev === "sectionNumber" ? null : "sectionNumber",
                    )
                  }
                >
                  <span className="flex items-center gap-0.5">
                    Section ↓
                    {poTableFilters.sectionNumber.length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                        {poTableFilters.sectionNumber.length}
                      </span>
                    )}
                  </span>
                  {showPoTableFilter === "sectionNumber" && (
                    <div
                      ref={poTableSectionRef}
                      className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                      style={{ minWidth: "220px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-3">
                        <Select
                          isMulti
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          options={poTableFilterOptions.sectionNumber.map(
                            (v) => ({ value: v, label: v }),
                          )}
                          value={poTableFilterOptions.sectionNumber
                            .map((v) => ({ value: v, label: v }))
                            .filter((opt) =>
                              poTableFilters.sectionNumber.includes(opt.value),
                            )}
                          onChange={(opts) =>
                            setPoTableFilters((prev) => ({
                              ...prev,
                              sectionNumber: opts
                                ? opts.map((o) => o.value)
                                : [],
                            }))
                          }
                          menuPortalTarget={document.body}
                          styles={multiSelectFilterStyles}
                        />
                      </div>
                    </div>
                  )}
                </th>

                {/* 3. Labor ID */}
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                  Labor ID
                </th>

                {/* 4. Description */}
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase min-w-[240px]">
                  Description
                </th>

                {/* 5. Size */}
                <th
                  className={`px-3 py-3 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 uppercase ${
                    poTableFilters.size.length > 0
                      ? "text-orange-400"
                      : "text-gray-400"
                  }`}
                  onClick={() =>
                    setShowPoTableFilter((prev) =>
                      prev === "size" ? null : "size",
                    )
                  }
                >
                  <span className="flex items-center gap-0.5">
                    Size ↓
                    {poTableFilters.size.length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                        {poTableFilters.size.length}
                      </span>
                    )}
                  </span>
                  {showPoTableFilter === "size" && (
                    <div
                      ref={poTableSizeRef}
                      className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                      style={{ minWidth: "200px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-3">
                        <Select
                          isMulti
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          options={poTableFilterOptions.size.map((v) => ({
                            value: v,
                            label: v,
                          }))}
                          value={poTableFilterOptions.size
                            .map((v) => ({ value: v, label: v }))
                            .filter((opt) =>
                              poTableFilters.size.includes(opt.value),
                            )}
                          onChange={(opts) =>
                            setPoTableFilters((prev) => ({
                              ...prev,
                              size: opts ? opts.map((o) => o.value) : [],
                            }))
                          }
                          menuPortalTarget={document.body}
                          styles={multiSelectFilterStyles}
                        />
                      </div>
                    </div>
                  )}
                </th>

                {/* 6. Net Qty */}
                <SortableNumericHeader label="Net Qty" sortKey="requiredQty" />
                {/* 7. Pending */}
                <SortableNumericHeader label="Pending" sortKey="qtyPending" />
                {/* 8. Ordered */}
                <SortableNumericHeader label="Ordered" sortKey="qtyOrdered" />
                {/* 9. Remaining */}
                <SortableNumericHeader label="Remaining" sortKey="remaining" />
                {/* 10. Unit Cost */}
                <SortableNumericHeader label="Unit Cost" sortKey="price" />
                {/* 11. Pkg Cost */}
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                  Pkg Cost
                </th>
                {/* 12. Unit */}
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase">
                  Unit
                </th>
                {/* 13. Vendor */}
                <th
                  className={`px-3 py-3 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 uppercase ${
                    poTableFilters.vendor.length > 0
                      ? "text-orange-400"
                      : "text-gray-400"
                  }`}
                  onClick={() =>
                    setShowPoTableFilter((prev) =>
                      prev === "vendor" ? null : "vendor",
                    )
                  }
                >
                  <span className="flex items-center gap-0.5">
                    Vendor ↓
                    {poTableFilters.vendor.length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                        {poTableFilters.vendor.length}
                      </span>
                    )}
                  </span>
                  {showPoTableFilter === "vendor" && (
                    <div
                      ref={poTableVendorRef}
                      className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                      style={{ minWidth: "280px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-3">
                        <Select
                          isMulti
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          options={poTableFilterOptions.vendor.map((v) => ({
                            value: v,
                            label: v,
                          }))}
                          value={poTableFilterOptions.vendor
                            .map((v) => ({ value: v, label: v }))
                            .filter((opt) =>
                              poTableFilters.vendor.includes(opt.value),
                            )}
                          onChange={(opts) =>
                            setPoTableFilters((prev) => ({
                              ...prev,
                              vendor: opts ? opts.map((o) => o.value) : [],
                            }))
                          }
                          menuPortalTarget={document.body}
                          styles={multiSelectFilterStyles}
                        />
                      </div>
                    </div>
                  )}
                </th>

                {/* 14. Bid Area */}
                <th
                  className={`px-3 py-3 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 uppercase ${
                    poTableFilters.bidArea.length > 0
                      ? "text-orange-400"
                      : "text-gray-400"
                  }`}
                  onClick={() =>
                    setShowPoTableFilter((prev) =>
                      prev === "bidArea" ? null : "bidArea",
                    )
                  }
                >
                  <span className="flex items-center gap-0.5">
                    Bid Area ↓
                    {poTableFilters.bidArea.length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                        {poTableFilters.bidArea.length}
                      </span>
                    )}
                  </span>
                  {showPoTableFilter === "bidArea" && (
                    <div
                      ref={poTableBidAreaRef}
                      className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                      style={{ minWidth: "280px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-3">
                        <Select
                          isMulti
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          options={poTableFilterOptions.bidArea.map((v) => ({
                            value: v,
                            label: v,
                          }))}
                          value={poTableFilterOptions.bidArea
                            .map((v) => ({ value: v, label: v }))
                            .filter((opt) =>
                              poTableFilters.bidArea.includes(opt.value),
                            )}
                          onChange={(opts) =>
                            setPoTableFilters((prev) => ({
                              ...prev,
                              bidArea: opts ? opts.map((o) => o.value) : [],
                            }))
                          }
                          menuPortalTarget={document.body}
                          styles={multiSelectFilterStyles}
                        />
                      </div>
                    </div>
                  )}
                </th>

                {/* 15. Amount */}
                <SortableNumericHeader label="Amount" sortKey="amount" />

                {editing && (
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {displayRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={totalColCount + (editing ? 0 : 1)}
                    className="px-4 py-12 text-center text-sm text-gray-400"
                  >
                    No material items match the current filters
                  </td>
                </tr>
              ) : (
                displayRows.map((row, idx) => {
                  // ── AGGREGATED ROW ──────────────────────────────────────────
                  if (groupBySimilar) {
                    const groupIsCoverage =
                      isSFUnit(row.uom) || isLFUnit(row.uom);
                    const groupCoverage =
                      getSafeCoverage(row.coveragePerUnit) || 1;
                    const groupPkgCost = groupIsCoverage
                      ? (row.price || 0) * groupCoverage
                      : row.price || 0;

                    return (
                      <tr
                        key={row.key || `group-${idx}`}
                        className="hover:bg-gray-600 transition-all duration-200"
                      >
                        {!editing && (
                          <td
                            className="w-8 px-2 py-3 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {(row.totalRemaining || 0) > 0 ? (
                              <input
                                type="checkbox"
                                checked={selectedRowKeys.has(row.key)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleRowSelect(row.key, e);
                                }}
                                className="h-3.5 w-3.5 rounded border-gray-500 text-orange-500 focus:ring-orange-500 cursor-pointer"
                              />
                            ) : (
                              <span className="block w-3.5 h-3.5" />
                            )}
                          </td>
                        )}
                        {!editing && <td className="w-6 px-1" />}

                        <td className="px-3 py-3 text-sm text-gray-100">
                          <div className="flex flex-col gap-1">
                            <span>{row.sampleCondition || "—"}</span>
                            <span className="text-xs text-gray-500">
                              ({row.count} similar items)
                            </span>
                          </div>
                        </td>
                        <td className="px-1 py-3 text-sm text-gray-300">
                          {row.sampleSection || "—"}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-500">—</td>
                        <td className="px-3 py-3 text-sm text-gray-100 min-w-[240px] max-w-[360px] whitespace-normal break-words">
                          {row.description}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-200">
                          {row.size || "—"}
                        </td>
                        <td className="px-3 py-3 text-right text-gray-200 font-semibold">
                          {formatNumber(row.totalNetQty)}
                        </td>
                        <td className="px-3 py-3 text-right text-yellow-400">
                          {formatNumber(row.totalPending)}
                        </td>
                        <td className="px-3 py-3 text-right text-green-400 font-medium">
                          {formatNumber(row.totalOrdered)}
                        </td>
                        <td className="px-3 py-3 text-right">
                          {row.totalRemaining > 0 ? (
                            <span className="text-orange-400 font-medium">
                              {formatNumber(row.totalRemaining)}
                            </span>
                          ) : (
                            <span className="text-gray-500">0</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span className="text-gray-200 font-mono">
                            ${formatCurrency(row.price)}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span className="font-mono font-medium text-orange-300">
                            ${formatCurrency(groupPkgCost)}
                          </span>
                          {groupIsCoverage && (
                            <div className="text-[10px] text-gray-500 text-right">
                              ×{groupCoverage} {row.uom}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-sm text-center text-gray-300">
                          {groupIsCoverage ? "PC" : row.uom || "—"}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-200">
                          {row.vendorName}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-400">—</td>
                        <td className="px-3 py-3 text-right font-semibold text-orange-400">
                          ${formatCurrency(row.totalAmount)}
                        </td>
                        {editing && (
                          <td className="px-3 py-3 text-center text-gray-500 text-xs">
                            —
                          </td>
                        )}
                      </tr>
                    );
                  }

                  // ── NORMAL ROW ──────────────────────────────────────────────
                  const isHistorical = row.isHistoricalPriceLine;
                  const isNewPrice = row.isPriceChangeRow && !isHistorical;
                  const isCoverageItem = isSFUnit(row.uom) || isLFUnit(row.uom);
                  const coverage = getSafeCoverage(row.coveragePerUnit);
                  const pkgCost = isCoverageItem
                    ? (row.price || 0) * coverage
                    : row.price || 0;

                  const rowKey = row._uniqueKey || `${row.lineItemId}-${idx}`;
                  const isExpanded = !editing && expandedRows.has(rowKey);
                  const hasOrderActivity =
                    (row.qtyOrdered || 0) > 0 || (row.qtyPending || 0) > 0;

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className={`
                          transition-colors
                          ${row.isNew ? "bg-green-900/20" : ""}
                          ${isHistorical ? "bg-gray-800/70 border-l-4 border-gray-500" : ""}
                          ${isNewPrice ? "bg-purple-900/15 border-l-4 border-purple-500" : ""}
                          ${row.closedOutBySizeChange ? "bg-yellow-900/10" : ""}
                          ${!editing && hasOrderActivity ? "cursor-pointer hover:bg-gray-600/80" : "hover:bg-gray-600"}
                          ${isExpanded ? "bg-gray-600/50" : ""}
                        `}
                        onClick={(e) => {
                          if (editing) {
                            handleRowClick(row, e);
                          } else if (
                            hasOrderActivity &&
                            !row.isHistoricalPriceLine
                          ) {
                            toggleRowExpand(rowKey, e);
                          }
                        }}
                      >
                        {!editing && (
                          <td
                            className="w-8 px-2 py-3 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {(row.remaining || 0) > 0 &&
                            !row.isHistoricalPriceLine &&
                            !row.closedOutBySizeChange ? (
                              <input
                                type="checkbox"
                                checked={selectedRowKeys.has(row.lineItemId)}
                                onChange={(e) =>
                                  toggleRowSelect(row.lineItemId, e)
                                }
                                className="h-3.5 w-3.5 rounded border-gray-500 text-orange-500 focus:ring-orange-500 cursor-pointer"
                              />
                            ) : (
                              <span className="block w-3.5 h-3.5" />
                            )}
                          </td>
                        )}
                        {/* Expand chevron */}
                        {!editing && (
                          <td className="w-6 px-1 py-3 text-center">
                            {hasOrderActivity && !row.isHistoricalPriceLine ? (
                              <span
                                className={`text-[10px] text-gray-500 transition-transform inline-block select-none ${isExpanded ? "rotate-90 text-orange-400" : ""}`}
                              >
                                ▶
                              </span>
                            ) : null}
                          </td>
                        )}

                        {/* Condition */}
                        <td className="px-3 py-3 text-sm text-gray-200">
                          <div className="flex flex-col gap-1">
                            <div className="whitespace-normal break-words min-w-[180px] max-w-[260px]">
                              {row.conditionName || "—"}
                            </div>
                            <div className="flex gap-1 flex-wrap mt-1">
                              {row.isNew && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-green-500/30 text-green-400 rounded font-medium">
                                  NEW
                                </span>
                              )}
                              <PriceChangeBadge item={row} />
                              {row.closedOutBySizeChange && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-yellow-500/30 text-yellow-400 rounded font-medium">
                                  CLOSED
                                </span>
                              )}
                              {row.isNewSpec && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-blue-500/30 text-blue-300 rounded font-medium">
                                  SIZE CHANGE
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Section */}
                        <td className="px-1 py-3 text-sm text-gray-200">
                          {row.sectionNumber || "—"}
                        </td>

                        {/* Labor ID */}
                        <td className="px-3 py-3 text-sm text-gray-300">
                          {row.laborId || "—"}
                        </td>

                        {/* Description */}
                        <td
                          className="px-3 py-3 text-sm text-gray-200 min-w-[240px] max-w-[360px] whitespace-normal break-words"
                          title={row.description}
                        >
                          <div className="line-clamp-3">
                            {row.description || "—"}
                          </div>
                          {row.note && (
                            <div className="text-[10px] text-amber-400 mt-1 italic">
                              {row.note}
                            </div>
                          )}
                        </td>

                        {/* Size */}
                        <td className="px-1 py-1 text-sm text-gray-200">
                          <div className="flex flex-col">
                            <span>{row.size || "—"}</span>
                            {row.isNewSpec && row.sizeChangedFrom && (
                              <span className="text-[10px] text-blue-400">
                                Was: {row.sizeChangedFrom}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Net Qty */}
                        <td className="px-3 py-3 text-sm text-right">
                          <span
                            className={
                              isHistorical
                                ? "text-gray-400"
                                : "text-gray-200 font-medium"
                            }
                          >
                            {formatNumber(row.requiredQty || 0)}
                          </span>
                        </td>

                        {/* Pending */}
                        <td className="px-3 py-3 text-sm text-yellow-400 text-right">
                          {row.qtyPending ? formatNumber(row.qtyPending) : "—"}
                        </td>

                        {/* Ordered */}
                        <td className="px-3 py-3 text-sm text-green-400 text-right font-medium">
                          {row.qtyOrdered ? formatNumber(row.qtyOrdered) : "—"}
                        </td>

                        {/* Remaining */}
                        <td className="px-3 py-3 text-sm text-right">
                          {isHistorical ? (
                            <span className="text-gray-500">—</span>
                          ) : row.remaining > 0 ? (
                            <span className="text-orange-400 font-medium">
                              {formatNumber(row.remaining)}
                            </span>
                          ) : (
                            <span className="text-gray-500">0</span>
                          )}
                        </td>

                        {/* Unit Cost */}
                        <td className="px-3 py-3 text-sm text-right">
                          <span
                            className={`font-mono ${
                              isHistorical
                                ? "text-gray-400"
                                : isNewPrice
                                  ? "text-purple-300"
                                  : "text-gray-200"
                            }`}
                          >
                            ${formatCurrency(row.price)}
                          </span>
                        </td>

                        {/* Pkg Cost */}
                        <td className="px-3 py-3 text-sm text-right">
                          <span
                            className={`font-mono font-medium ${
                              isHistorical ? "text-gray-400" : "text-orange-300"
                            }`}
                          >
                            ${formatCurrency(pkgCost)}
                          </span>
                          {isCoverageItem && (
                            <div className="text-[10px] text-gray-500 text-right">
                              ×{coverage} {row.uom}
                            </div>
                          )}
                        </td>

                        {/* UOM */}
                        <td className="px-3 py-3 text-sm text-center text-gray-300">
                          {isCoverageItem ? "PC" : row.uom || "—"}
                        </td>

                        {/* Vendor */}
                        <td className="px-3 py-3 text-sm text-gray-200">
                          {row.vendorName || "—"}
                        </td>

                        {/* Bid Area */}
                        <td className="px-3 py-3 text-sm text-gray-200">
                          {row.bidArea || "—"}
                        </td>

                        {/* Amount */}
                        <td className="px-3 py-3 text-sm text-right font-medium">
                          <span
                            className={
                              isHistorical ? "text-gray-400" : "text-gray-200"
                            }
                          >
                            ${formatCurrency(row.amount)}
                          </span>
                        </td>

                        {/* Actions (edit mode) */}
                        {editing && (
                          <td className="px-3 py-3 text-center text-sm">
                            {row.isHistoricalPriceLine ? (
                              <span className="text-gray-600 text-xs">—</span>
                            ) : row.isNew ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveFromAdd(row);
                                }}
                                className="text-red-400 hover:text-red-300 font-medium"
                              >
                                Cancel
                              </button>
                            ) : row.closedOutBySizeChange ? (
                              <span className="text-gray-600">—</span>
                            ) : isMarkedForRemoval(row) ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUndoRemove(row);
                                }}
                                className="px-2 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded text-xs font-semibold transition-colors"
                              >
                                Undo Remove
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveItem(row);
                                }}
                                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                  (row.qtyPending || 0) > 0 ||
                                  (row.qtyOrdered || 0) > 0
                                    ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                                    : "bg-red-600/80 hover:bg-red-500 text-white"
                                }`}
                                disabled={
                                  (row.qtyPending || 0) > 0 ||
                                  (row.qtyOrdered || 0) > 0
                                }
                                title={
                                  (row.qtyPending || 0) > 0 ||
                                  (row.qtyOrdered || 0) > 0
                                    ? "Cannot remove items with pending or ordered quantities"
                                    : "Click to remove from PO"
                                }
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        )}
                      </tr>

                      {/* ── EXPANDED MO BREAKDOWN ROW ── */}

                      {/* ── EXPANDED MO BREAKDOWN ROW ── */}
                      {isExpanded && projectId && (
                        <MOBreakdownRow
                          lineItemId={row.lineItemId}
                          projectId={projectId}
                          colSpan={totalColCount + 1}
                          navigate={navigate}
                          id={id}
                          formatNumber={formatNumber}
                          vendorId={row.vendorId?.toString()}
                          isHistoricalPriceLine={
                            row.isHistoricalPriceLine || false
                          }
                          isPriceChangeRow={row.isPriceChangeRow || false}
                          isNewSpec={row.isNewSpec || false}
                          closedOutBySizeChange={
                            row.closedOutBySizeChange || false
                          }
                          rowQtyOrdered={row.qtyOrdered || 0}
                        />
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer totals */}
        <div className="mt-4 bg-gray-900/70 p-3 rounded-lg border border-gray-700 text-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-gray-400">Filtered Materials:</span>{" "}
              <span className="font-medium text-white">
                {materialTotals.count}
              </span>
            </div>
            <div>
              <span className="text-gray-400 text-xl">Estimated Total:</span>{" "}
              <span className="font-medium text-orange-400 text-xl">
                $
                {materialTotals.totalAmount.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      </div>
      <VendorSwitchModal
        isOpen={showVendorSwitchModal}
        onClose={() => {
          setShowVendorSwitchModal(false);
          setSelectedForVendorSwitch([]);
        }}
        onConfirm={handleVendorSwitchConfirm}
        selectedItems={selectedForVendorSwitch}
        vendors={vendors}
        groupBySimilar={groupBySimilar}
      />
    </div>
  );
}
